import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Package, Shield, Search, UserX, UserCheck,
  RefreshCw, CheckCircle, Receipt, X, FileSpreadsheet,
  ChevronLeft, ChevronRight, Edit3, ChevronDown, Key, Copy, Eye, EyeOff, Trash2, RotateCcw, Clock, Activity, Calendar, User, Check
} from 'lucide-react';
import { supabase, signOut, fetchAllRows } from '../lib/supabase';
import { getCurrentUser, isOverrideAdmin } from '../api/auth';
import { createAgentAccount } from '../api/admin';
import {
  getApiKeys, generateApiKey, revokeApiKey, rotateApiKey, getApiKeyUsageLogs,
  type ApiKeyRecord, type ApiUsageLog
} from '../api/apiKeys';
import {
  getAllDialerSubscriptions, createDialerSubscription, startCampaign,
  updateCredentials, expireSubscription, renewSubscription, deleteSubscription,
  checkExpirations, computeStats, getDaysRemaining, updateSubscriptionDates,
  updateSubscriptionProduct,
  DIALER_PRODUCT_IDS, DEFAULT_DURATIONS, DIALER_PRODUCT_NAMES, DIALER_PRODUCT_PRICES,
  resolveDialerProductId, isMissedInboundDialerSub, isOctoberMissedInboundDialerSub,
  type DialerSubWithAgent, type DialerSubscriptionStatus,
} from '../api/dialerSubscriptions';
import { getStoreOrders, getStoreOrderStats, type StoreOrder } from '../api/storeOrders';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import Header from './Header';
import InboundInquiries from './InboundInquiries';
import { products, agedProducts, smsProducts } from '../data/products';
import { siteConfig } from '../config/site';

interface Agent {
  id: string;
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  status: string;
  role: string;
  created_at: string;
  avatar_url?: string;
  created_by?: string;
}

interface Order {
  id: string;
  agent_id: string;
  product_name: string;
  product_category: string;
  quantity: number;
  price: number;
  status: string;
  created_at: string;
  paid_at?: string;
  shopify_order_id?: string;
  states?: string[];
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  notes?: string;
  fulfilled?: boolean;
  fulfilled_at?: string | null;
  google_sheet_url?: string | null;
}

interface DailySales {
  date: string;
  total: number;
  count: number;
}

interface CreateSubModalState {
  isOpen: boolean; agentId: string; productId: number; productIds: number[]; duration: number; notes: string;
  username: string; password: string; isFree: boolean; customStartDate: string; customExpiryDate: string;
}

const CreateSubscriptionModal: React.FC<{
  agents: Agent[];
  dialerCreateModal: CreateSubModalState;
  setDialerCreateModal: React.Dispatch<React.SetStateAction<CreateSubModalState>>;
  handleDialerCreate: () => void;
  dialerActionLoading: string | null;
}> = ({ agents, dialerCreateModal, setDialerCreateModal, handleDialerCreate, dialerActionLoading }) => {
  const [agentDropOpen, setAgentDropOpen] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const agentDropRef = React.useRef<HTMLDivElement>(null);

  const DURATION_PRESETS = [7, 14, 30, 60, 90];
  const isUnlimited = dialerCreateModal.duration === -1;
  const isCustomDuration = !isUnlimited && !DURATION_PRESETS.includes(dialerCreateModal.duration);
  const closeModal = () => setDialerCreateModal({ isOpen: false, agentId: '', productId: DIALER_PRODUCT_IDS[0], productIds: [], duration: DEFAULT_DURATIONS[DIALER_PRODUCT_IDS[0]], notes: '', username: '', password: '', isFree: false, customStartDate: '', customExpiryDate: '' });

  const sortedAgents = agents.filter(a => a.status === 'active').sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  const filteredAgents = agentSearch ? sortedAgents.filter(a => `${a.first_name} ${a.last_name}`.toLowerCase().includes(agentSearch.toLowerCase())) : sortedAgents;
  const selectedAgentObj = sortedAgents.find(a => a.user_id === dialerCreateModal.agentId);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (agentDropRef.current && !agentDropRef.current.contains(e.target as Node)) {
        setAgentDropOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const splitDateTime = (iso: string) => {
    if (!iso) return { date: '', time: '' };
    const parts = iso.split('T');
    return { date: parts[0] || '', time: parts[1]?.substring(0, 5) || '' };
  };
  const mergeDateTime = (date: string, time: string) => {
    if (!date) return '';
    return time ? `${date}T${time}` : `${date}T00:00`;
  };

  const startParts = splitDateTime(dialerCreateModal.customStartDate);
  const expiryParts = splitDateTime(dialerCreateModal.customExpiryDate);

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto py-6 sm:py-10" onClick={closeModal}>
      <div className="fixed inset-0 bg-black/40" />
      <div className="relative w-full max-w-[520px] mx-4 animate-[modalSlideIn_0.18s_ease-out]" onClick={e => { e.stopPropagation(); setAgentDropOpen(false); }}>
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.2),0_0_0_1px_rgba(255,255,255,0.7)_inset] border border-white/60 overflow-visible">
          {/* Header */}
          <div className="px-6 pt-6 pb-4 bg-gradient-to-b from-slate-50/80 to-transparent">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-lg font-bold text-slate-900 tracking-tight">New Subscription</h2>
              <button onClick={closeModal} className="w-8 h-8 rounded-xl bg-slate-100/80 hover:bg-slate-200/80 flex items-center justify-center transition-all -mr-1">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <p className="text-[13px] text-slate-400">Create a dialer subscription for an agent.</p>
          </div>

          <div className="px-6 pb-5 space-y-4">
            {/* Agent Selector - Custom dropdown */}
            <div ref={agentDropRef} onClick={e => e.stopPropagation()}>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Agent</label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => { setAgentDropOpen(!agentDropOpen); setAgentSearch(''); }}
                  className={`w-full flex items-center gap-2.5 pl-3.5 pr-3 py-2.5 bg-white/70 backdrop-blur-sm border rounded-xl text-sm text-left transition-all shadow-sm hover:shadow-md cursor-pointer ${
                    agentDropOpen ? 'border-blue-400 ring-2 ring-blue-500/20 shadow-md' : 'border-slate-200/80 hover:border-slate-300'
                  }`}
                >
                  <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                  <span className={`flex-1 truncate ${selectedAgentObj ? 'text-slate-800 font-medium' : 'text-slate-400'}`}>
                    {selectedAgentObj ? `${selectedAgentObj.first_name} ${selectedAgentObj.last_name}` : 'Select an agent...'}
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 flex-shrink-0 ${agentDropOpen ? 'rotate-180' : ''}`} />
                </button>
                {agentDropOpen && (
                  <div className="absolute z-[70] left-0 right-0 mt-1.5 bg-white/95 backdrop-blur-xl border border-slate-200/80 rounded-xl shadow-[0_12px_40px_-8px_rgba(0,0,0,0.15)] overflow-hidden animate-[modalSlideIn_0.12s_ease-out]">
                    <div className="p-2 border-b border-slate-100">
                      <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                        <input
                          type="text"
                          value={agentSearch}
                          onChange={e => setAgentSearch(e.target.value)}
                          placeholder="Search agents..."
                          autoFocus
                          className="w-full pl-8 pr-3 py-1.5 bg-slate-50/80 border border-slate-200/60 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500/20 focus:border-blue-300 placeholder:text-slate-400"
                        />
                      </div>
                    </div>
                    <div className="max-h-48 overflow-y-auto py-1">
                      {filteredAgents.length === 0 ? (
                        <div className="px-3 py-4 text-center text-xs text-slate-400">No agents found</div>
                      ) : filteredAgents.map(a => (
                        <button
                          key={a.user_id}
                          type="button"
                          onClick={() => { setDialerCreateModal(prev => ({ ...prev, agentId: a.user_id })); setAgentDropOpen(false); }}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                            dialerCreateModal.agentId === a.user_id
                              ? 'bg-blue-50 text-blue-700'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-[10px] font-bold text-slate-600 flex-shrink-0">
                            {a.first_name?.[0]}{a.last_name?.[0]}
                          </div>
                          <span className="flex-1 truncate">{a.first_name} {a.last_name}</span>
                          {dialerCreateModal.agentId === a.user_id && <Check className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Product Selector - Multi-select checkboxes */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
                Products
                <span className="text-slate-400 font-normal normal-case ml-1">(select one or more)</span>
              </label>
              <div className="space-y-1.5">
                {DIALER_PRODUCT_IDS.map(pid => {
                  const isSelected = dialerCreateModal.productIds.includes(pid);
                  return (
                    <button
                      key={pid}
                      type="button"
                      onClick={() => {
                        setDialerCreateModal(prev => {
                          const ids = prev.productIds.includes(pid)
                            ? prev.productIds.filter(id => id !== pid)
                            : [...prev.productIds, pid];
                          return { ...prev, productIds: ids, productId: ids[0] ?? DIALER_PRODUCT_IDS[0] };
                        });
                      }}
                      className={`w-full flex items-center justify-between px-3.5 py-2.5 rounded-xl text-sm transition-all ${
                        isSelected
                          ? 'bg-blue-50 border-2 border-blue-400 text-blue-700'
                          : 'bg-white/70 border border-slate-200/80 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className={`w-4 h-4 rounded flex items-center justify-center border ${
                          isSelected ? 'bg-blue-500 border-blue-500' : 'border-slate-300 bg-white'
                        }`}>
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <span className="font-medium">{DIALER_PRODUCT_NAMES[pid]}</span>
                      </div>
                      <span className={`text-xs ${isSelected ? 'text-blue-500' : 'text-slate-400'}`}>${DIALER_PRODUCT_PRICES[pid]}</span>
                    </button>
                  );
                })}
              </div>
              {dialerCreateModal.productIds.length > 1 && (
                <p className="text-[11px] text-blue-500 mt-2 font-medium">
                  {dialerCreateModal.productIds.length} products selected. One subscription will be created per product
                </p>
              )}
            </div>

            {/* Duration - Pill presets + custom */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Duration</label>
              <div className="flex flex-wrap gap-2">
                {DURATION_PRESETS.map(d => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDialerCreateModal(prev => ({ ...prev, duration: d }))}
                    className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      dialerCreateModal.duration === d && !isCustomDuration && !isUnlimited
                        ? 'bg-slate-800 text-white shadow-md'
                        : 'bg-white/70 backdrop-blur-sm text-slate-600 border border-slate-200/80 hover:border-slate-300 hover:bg-white shadow-sm hover:shadow-md'
                    }`}
                  >
                    {d}d
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setDialerCreateModal(prev => ({ ...prev, duration: -1 }))}
                  className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all ${
                    isUnlimited
                      ? 'bg-slate-800 text-white shadow-md'
                      : 'bg-white/70 backdrop-blur-sm text-slate-600 border border-slate-200/80 hover:border-slate-300 hover:bg-white shadow-sm hover:shadow-md'
                  }`}
                >
                  ∞
                </button>
                <div className="relative">
                  <input
                    type="number"
                    min={1}
                    placeholder="Custom"
                    value={isCustomDuration ? dialerCreateModal.duration : ''}
                    onChange={e => {
                      const v = parseInt(e.target.value);
                      if (v > 0) setDialerCreateModal(prev => ({ ...prev, duration: v }));
                    }}
                    onFocus={e => { if (!isCustomDuration) (e.target as HTMLInputElement).value = ''; }}
                    className={`w-[80px] px-3 py-1.5 rounded-lg text-sm font-medium text-center transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20 ${
                      isCustomDuration
                        ? 'bg-slate-800 text-white border-transparent shadow-md'
                        : 'bg-white/70 backdrop-blur-sm text-slate-500 border border-slate-200/80 hover:border-slate-300 shadow-sm placeholder:text-slate-400'
                    }`}
                  />
                </div>
              </div>
            </div>

            {/* Free toggle */}
            <div className="flex items-center justify-between bg-gradient-to-r from-amber-50/50 to-orange-50/50 backdrop-blur-sm border border-amber-200/40 rounded-xl px-4 py-3">
              <div>
                <span className="text-sm font-medium text-slate-700">Free / Complimentary</span>
                <p className="text-[11px] text-slate-400 mt-0.5">No charge for this subscription</p>
              </div>
              <button
                type="button"
                onClick={() => setDialerCreateModal(prev => ({ ...prev, isFree: !prev.isFree }))}
                className={`relative w-11 h-6 rounded-full transition-all duration-200 ${dialerCreateModal.isFree ? 'bg-amber-500 shadow-amber-500/25 shadow-lg' : 'bg-slate-300'}`}
              >
                <div className={`absolute top-[3px] w-[18px] h-[18px] bg-white rounded-full shadow-sm transition-transform duration-200 ${dialerCreateModal.isFree ? 'translate-x-[22px]' : 'translate-x-[3px]'}`} />
              </button>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-1.5">Notes <span className="text-slate-400 font-normal normal-case">(optional)</span></label>
              <textarea
                value={dialerCreateModal.notes}
                onChange={e => setDialerCreateModal(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                placeholder="Any notes for this subscription..."
                className="w-full px-3.5 py-2.5 bg-white/60 backdrop-blur-sm border border-slate-200/80 rounded-xl text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all resize-none placeholder:text-slate-300 shadow-sm"
              />
            </div>

            {/* Custom Dates Section */}
            <div className="bg-gradient-to-r from-blue-50/40 to-indigo-50/40 backdrop-blur-sm border border-blue-200/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Calendar className="w-4 h-4 text-blue-500" />
                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">Custom Dates</span>
                <span className="text-[10px] text-slate-400 font-normal">(for historical entries)</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={startParts.date}
                    onChange={e => setDialerCreateModal(prev => ({ ...prev, customStartDate: mergeDateTime(e.target.value, startParts.time) }))}
                    className="w-full px-3 py-2 bg-white/80 border border-blue-200/60 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Start Time</label>
                  <input
                    type="time"
                    value={startParts.time}
                    onChange={e => setDialerCreateModal(prev => ({ ...prev, customStartDate: mergeDateTime(startParts.date, e.target.value) }))}
                    className="w-full px-3 py-2 bg-white/80 border border-blue-200/60 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all shadow-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Expiry Date</label>
                  <input
                    type="date"
                    value={expiryParts.date}
                    onChange={e => setDialerCreateModal(prev => ({ ...prev, customExpiryDate: mergeDateTime(e.target.value, expiryParts.time) }))}
                    className="w-full px-3 py-2 bg-white/80 border border-blue-200/60 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Expiry Time</label>
                  <input
                    type="time"
                    value={expiryParts.time}
                    onChange={e => setDialerCreateModal(prev => ({ ...prev, customExpiryDate: mergeDateTime(expiryParts.date, e.target.value) }))}
                    className="w-full px-3 py-2 bg-white/80 border border-blue-200/60 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all shadow-sm"
                  />
                </div>
              </div>
              {(dialerCreateModal.customStartDate || dialerCreateModal.customExpiryDate) && (
                <button
                  type="button"
                  onClick={() => setDialerCreateModal(prev => ({ ...prev, customStartDate: '', customExpiryDate: '' }))}
                  className="mt-2 text-[11px] text-blue-500 hover:text-blue-700 font-medium transition-colors"
                >
                  Clear dates
                </button>
              )}
            </div>

            {/* Credentials Section */}
            <div className="bg-gradient-to-r from-emerald-50/40 to-teal-50/40 backdrop-blur-sm border border-emerald-200/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Key className="w-4 h-4 text-emerald-500" />
                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wider">{dialerCreateModal.customStartDate ? 'Credentials' : 'Immediate Activation'}</span>
              </div>
              {!dialerCreateModal.customStartDate && (
                <p className="text-[11px] text-slate-400 mb-3 ml-6">Leave blank to create as pending. Fill both to activate now.</p>
              )}
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Username</label>
                  <input
                    type="text"
                    value={dialerCreateModal.username}
                    onChange={e => setDialerCreateModal(prev => ({ ...prev, username: e.target.value }))}
                    placeholder="Optional"
                    className="w-full px-3 py-2 bg-white/80 border border-emerald-200/60 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all placeholder:text-slate-300 shadow-sm"
                  />
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">Password</label>
                  <input
                    type="text"
                    value={dialerCreateModal.password}
                    onChange={e => setDialerCreateModal(prev => ({ ...prev, password: e.target.value }))}
                    placeholder="Optional"
                    className="w-full px-3 py-2 bg-white/80 border border-emerald-200/60 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 transition-all placeholder:text-slate-300 shadow-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="px-6 pb-6 flex gap-3">
            <button onClick={closeModal} className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100/80 hover:bg-slate-200/80 rounded-xl transition-all">Cancel</button>
            <button
              onClick={handleDialerCreate}
              disabled={!dialerCreateModal.agentId || dialerCreateModal.productIds.length === 0 || dialerActionLoading === 'create'}
              className={`flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-xl transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg ${
                dialerCreateModal.isFree
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-amber-500/25'
                  : dialerCreateModal.username.trim() && dialerCreateModal.password.trim()
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-emerald-500/25'
                    : 'bg-gradient-to-r from-slate-700 to-slate-900 hover:from-slate-800 hover:to-slate-950 shadow-slate-800/25'
              }`}
            >
              {dialerActionLoading === 'create' && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
              {dialerCreateModal.isFree ? 'Create Free Sub' : dialerCreateModal.username.trim() && dialerCreateModal.password.trim() ? 'Create & Activate' : 'Create Subscription'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminName, setAdminName] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'orders' | 'products' | 'api-keys' | 'dialer-subs' | 'store-orders' | 'inbound-inquiries'>('overview');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [orderFilter, setOrderFilter] = useState<'all' | 'fulfilled'>('all');
  const [viewStatesOrderId, setViewStatesOrderId] = useState<string | null>(null);
  
  const [agents, setAgents] = useState<Agent[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Fulfillment modal state
  const [fulfillmentModal, setFulfillmentModal] = useState<{
    isOpen: boolean;
    order: Order | null;
    googleSheetUrl: string;
  }>({ isOpen: false, order: null, googleSheetUrl: '' });
  
  // Manual order modal state
  const [manualOrderModal, setManualOrderModal] = useState<{
    isOpen: boolean;
    editMode: boolean;
    editOrderId: string | null;
    agentId: string;
    productCategory: 'Fresh Leads' | 'Aged Leads' | 'SMS Leads' | 'Dialer Technology';
    selectedProductId: number | null;
    selectedPackageIndex: number | null;
    customProductName: string;
    customQuantity: number;
    customPrice: number;
    useCustom: boolean;
    googleSheetUrl: string;
    states: string[];
    notes: string;
    showStatesDropdown: boolean;
  }>({
    isOpen: false,
    editMode: false,
    editOrderId: null,
    agentId: '',
    productCategory: 'Fresh Leads',
    selectedProductId: null,
    selectedPackageIndex: null,
    customProductName: '',
    customQuantity: 0,
    customPrice: 0,
    useCustom: false,
    googleSheetUrl: '',
    states: [],
    notes: '',
    showStatesDropdown: false
  });
  
  // Pagination state for orders
  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [currentOrderPage, setCurrentOrderPage] = useState(1);
  
  // Pagination state for agents
  const [agentsPerPage, setAgentsPerPage] = useState(10);
  const [currentAgentPage, setCurrentAgentPage] = useState(1);

  // Create agent modal state
  const [createAgentModal, setCreateAgentModal] = useState<{
    isOpen: boolean;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    password: string;
    confirmPassword: string;
    showPassword: boolean;
    isSubmitting: boolean;
    error: string | null;
    success: string | null;
  }>({
    isOpen: false,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    showPassword: false,
    isSubmitting: false,
    error: null,
    success: null,
  });

  // Pagination state for dialer subs
  const [dialerSubsPerPage, setDialerSubsPerPage] = useState(10);
  const [currentDialerPage, setCurrentDialerPage] = useState(1);
  
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [last7DaysRevenue, setLast7DaysRevenue] = useState(0);
  const [thisWeekRevenue, setThisWeekRevenue] = useState(0);
  const [monthRevenue, setMonthRevenue] = useState(0);
  const [ytdRevenue, setYtdRevenue] = useState(0);
  const [lastWeekRevenue, setLastWeekRevenue] = useState(0);
  const [prevWeekRevenue, setPrevWeekRevenue] = useState(0);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [activeAgents, setActiveAgents] = useState(0);
  const [disabledAgents, setDisabledAgents] = useState(0);
  const [newSignupsThisWeek, setNewSignupsThisWeek] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const [todayOrders, setTodayOrders] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const [unfulfilledOrders, setUnfulfilledOrders] = useState(0);
  const [avgOrderValue, setAvgOrderValue] = useState(0);
  const [topProduct, setTopProduct] = useState<string>('');
  const [fulfillmentRate, setFulfillmentRate] = useState(0);
  const [dailySales, setDailySales] = useState<DailySales[]>([]);
  const [monthlySales, setMonthlySales] = useState<DailySales[]>([]);
  const [yearlyMonthSales, setYearlyMonthSales] = useState<DailySales[]>([]);
  const [productBreakdown, setProductBreakdown] = useState<{name: string; count: number; revenue: number}[]>([]);

  // API Keys state
  const [apiKeys, setApiKeys] = useState<ApiKeyRecord[]>([]);
  const [apiKeysLoading, setApiKeysLoading] = useState(false);
  const [showNewKeyModal, setShowNewKeyModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
  const [newlyCreatedRecoveryCode, setNewlyCreatedRecoveryCode] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);
  const [recoveryCopied, setRecoveryCopied] = useState(false);
  const [showRotateModal, setShowRotateModal] = useState<{ isOpen: boolean; keyId: string; keyName: string }>({ isOpen: false, keyId: '', keyName: '' });
  const [rotatePassword, setRotatePassword] = useState('');
  const [rotateError, setRotateError] = useState('');
  const [selectedKeyLogs, setSelectedKeyLogs] = useState<{ keyId: string; keyName: string; logs: ApiUsageLog[] } | null>(null);
  const [showKeyValue, setShowKeyValue] = useState(false);

  // Dialer Subscriptions state
  const [dialerSubs, setDialerSubs] = useState<DialerSubWithAgent[]>([]);
  const [dialerLoading, setDialerLoading] = useState(false);
  const [storeOrders, setStoreOrders] = useState<StoreOrder[]>([]);
  const [storeOrdersLoading, setStoreOrdersLoading] = useState(false);
  const [expandedStoreOrderId, setExpandedStoreOrderId] = useState<string | null>(null);
  const [dialerFilter, setDialerFilter] = useState<'all' | 'free' | DialerSubscriptionStatus>('all');
  const [dialerSearch, setDialerSearch] = useState('');
  const [dialerStartModal, setDialerStartModal] = useState<{
    isOpen: boolean; subId: string | null; username: string; password: string; duration: number;
  }>({ isOpen: false, subId: null, username: '', password: '', duration: 30 });
  const [dialerEditCredsModal, setDialerEditCredsModal] = useState<{
    isOpen: boolean; subId: string | null; username: string; password: string;
  }>({ isOpen: false, subId: null, username: '', password: '' });
  const [dialerCreateModal, setDialerCreateModal] = useState<CreateSubModalState>({ isOpen: false, agentId: '', productId: DIALER_PRODUCT_IDS[0], productIds: [], duration: DEFAULT_DURATIONS[DIALER_PRODUCT_IDS[0]], notes: '', username: '', password: '', isFree: false, customStartDate: '', customExpiryDate: '' });
  const [dialerRenewModal, setDialerRenewModal] = useState<{
    isOpen: boolean; subId: string | null; additionalDays: number; currentExpiry: string | null; productId: number;
  }>({ isOpen: false, subId: null, additionalDays: 30, currentExpiry: null, productId: DIALER_PRODUCT_IDS[0] });
  const [dialerActionLoading, setDialerActionLoading] = useState<string | null>(null);
  const [dialerEditDatesModal, setDialerEditDatesModal] = useState<{
    isOpen: boolean; subId: string | null; startedAt: string; expiresAt: string;
  }>({ isOpen: false, subId: null, startedAt: '', expiresAt: '' });
  const [expandedDialerProducts, setExpandedDialerProducts] = useState<Set<number>>(new Set());
  const [dialerChangeProductModal, setDialerChangeProductModal] = useState<{
    isOpen: boolean; subId: string | null; currentProductId: number; newProductId: number;
  }>({ isOpen: false, subId: null, currentProductId: 0, newProductId: 0 });
  const [dialerEditStatesModal, setDialerEditStatesModal] = useState<{
    isOpen: boolean; subId: string | null; orderId: string | null; states: string;
  }>({ isOpen: false, subId: null, orderId: null, states: '' });
  const [selectedDialerSubs, setSelectedDialerSubs] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (activeTab === 'api-keys') loadApiKeys();
    if (activeTab === 'dialer-subs') loadDialerSubs();
    if (activeTab === 'store-orders') loadStoreOrders();
  }, [activeTab]);

  useEffect(() => {
    checkAdminAndLoadData();

    const agentsSubscription = supabase
      .channel('agents-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'agents' }, () => {
        loadAllData();
      })
      .subscribe();

    const ordersSubscription = supabase
      .channel('orders-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'orders' }, () => {
        loadAllData();
      })
      .subscribe();

    const dialerSubscription = supabase
      .channel('dialer-subs-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'dialer_subscriptions' }, () => {
        if (activeTab === 'dialer-subs') loadDialerSubs();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(agentsSubscription);
      supabase.removeChannel(ordersSubscription);
      supabase.removeChannel(dialerSubscription);
    };
  }, []);

  // ==================== STORE ORDERS ====================
  const loadStoreOrders = async () => {
    setStoreOrdersLoading(true);
    try {
      const result = await getStoreOrders();
      if (result.success && result.data) {
        setStoreOrders(result.data);
      }
    } catch (error) {
      console.error('Error loading store orders:', error);
    } finally {
      setStoreOrdersLoading(false);
    }
  };

  // ==================== DIALER SUBSCRIPTIONS ====================
  const loadDialerSubs = async () => {
    setDialerLoading(true);
    try {
      await checkExpirations();
      const result = await getAllDialerSubscriptions();
      if (result.success && result.data) {
        setDialerSubs(result.data);
      }
    } catch (error) {
      console.error('Error loading dialer subs:', error);
    } finally {
      setDialerLoading(false);
    }
  };

  const handleDialerStartCampaign = async () => {
    if (!dialerStartModal.subId || !dialerStartModal.username.trim() || !dialerStartModal.password.trim()) return;
    setDialerActionLoading(dialerStartModal.subId);
    try {
      const result = await startCampaign({
        subscriptionId: dialerStartModal.subId,
        dialerUsername: dialerStartModal.username.trim(),
        dialerPassword: dialerStartModal.password.trim(),
        durationDays: dialerStartModal.duration,
      });
      if (!result.success) throw new Error(result.error);
      setDialerStartModal({ isOpen: false, subId: null, username: '', password: '', duration: 30 });
      await loadDialerSubs();
    } catch (error: any) {
      alert(error.message ?? 'Failed to start campaign');
    } finally {
      setDialerActionLoading(null);
    }
  };

  const handleDialerUpdateCreds = async () => {
    if (!dialerEditCredsModal.subId || !dialerEditCredsModal.username.trim() || !dialerEditCredsModal.password.trim()) return;
    setDialerActionLoading(dialerEditCredsModal.subId);
    try {
      const result = await updateCredentials(dialerEditCredsModal.subId, dialerEditCredsModal.username.trim(), dialerEditCredsModal.password.trim());
      if (!result.success) throw new Error(result.error);
      setDialerEditCredsModal({ isOpen: false, subId: null, username: '', password: '' });
      await loadDialerSubs();
    } catch (error: any) {
      alert(error.message ?? 'Failed to update credentials');
    } finally {
      setDialerActionLoading(null);
    }
  };

  const handleDialerExpire = async (subId: string) => {
    if (!confirm('Are you sure you want to expire this subscription?')) return;
    setDialerActionLoading(subId);
    try {
      const result = await expireSubscription(subId);
      if (!result.success) throw new Error(result.error);
      await loadDialerSubs();
    } catch (error: any) {
      alert(error.message ?? 'Failed to expire subscription');
    } finally {
      setDialerActionLoading(null);
    }
  };

  const handleDialerCreate = async () => {
    if (!dialerCreateModal.agentId) return;
    const productIdsToCreate = dialerCreateModal.productIds.length > 0
      ? dialerCreateModal.productIds
      : [dialerCreateModal.productId];
    setDialerActionLoading('create');
    try {
      const hasCustomDates = dialerCreateModal.customStartDate || dialerCreateModal.customExpiryDate;
      const isUnlimitedDuration = dialerCreateModal.duration === -1;
      const errors: string[] = [];

      for (const pid of productIdsToCreate) {
        const result = await createDialerSubscription({
          agent_id: dialerCreateModal.agentId,
          product_id: pid,
          product_name: DIALER_PRODUCT_NAMES[pid],
          product_price: DIALER_PRODUCT_PRICES[pid],
          duration_days: isUnlimitedDuration ? -1 : dialerCreateModal.duration,
          notes: dialerCreateModal.notes.trim() || undefined,
          is_free: dialerCreateModal.isFree,
          is_unlimited: isUnlimitedDuration,
          custom_started_at: dialerCreateModal.customStartDate ? new Date(dialerCreateModal.customStartDate).toISOString() : undefined,
          custom_expires_at: isUnlimitedDuration ? undefined : (dialerCreateModal.customExpiryDate ? new Date(dialerCreateModal.customExpiryDate).toISOString() : undefined),
        });
        if (!result.success) { errors.push(`${DIALER_PRODUCT_NAMES[pid]}: ${result.error}`); continue; }

        if (!hasCustomDates && dialerCreateModal.username.trim() && dialerCreateModal.password.trim() && result.data) {
          const activateResult = await startCampaign({
            subscriptionId: result.data.id,
            dialerUsername: dialerCreateModal.username.trim(),
            dialerPassword: dialerCreateModal.password.trim(),
            durationDays: isUnlimitedDuration ? -1 : dialerCreateModal.duration,
          });
          if (!activateResult.success) errors.push(`${DIALER_PRODUCT_NAMES[pid]} activation: ${activateResult.error}`);
        } else if (hasCustomDates && dialerCreateModal.username.trim() && dialerCreateModal.password.trim() && result.data) {
          await updateCredentials(result.data.id, dialerCreateModal.username.trim(), dialerCreateModal.password.trim());
        }
      }

      if (errors.length > 0) alert('Some subscriptions had issues:\n' + errors.join('\n'));

      const resetState: CreateSubModalState = { isOpen: false, agentId: '', productId: DIALER_PRODUCT_IDS[0], productIds: [], duration: DEFAULT_DURATIONS[DIALER_PRODUCT_IDS[0]], notes: '', username: '', password: '', isFree: false, customStartDate: '', customExpiryDate: '' };
      setDialerCreateModal(resetState);
      await loadDialerSubs();
    } catch (error: any) {
      alert(error.message ?? 'Failed to create subscription');
    } finally {
      setDialerActionLoading(null);
    }
  };

  const handleDialerEditDates = async () => {
    if (!dialerEditDatesModal.subId) return;
    setDialerActionLoading(dialerEditDatesModal.subId);
    try {
      const result = await updateSubscriptionDates(
        dialerEditDatesModal.subId,
        dialerEditDatesModal.startedAt ? new Date(dialerEditDatesModal.startedAt).toISOString() : null,
        dialerEditDatesModal.expiresAt ? new Date(dialerEditDatesModal.expiresAt).toISOString() : null,
      );
      if (!result.success) throw new Error(result.error);
      setDialerEditDatesModal({ isOpen: false, subId: null, startedAt: '', expiresAt: '' });
      await loadDialerSubs();
    } catch (error: any) {
      alert(error.message ?? 'Failed to update dates');
    } finally {
      setDialerActionLoading(null);
    }
  };

  const handleDialerRenew = async () => {
    if (!dialerRenewModal.subId || dialerRenewModal.additionalDays < 1) return;
    setDialerActionLoading(dialerRenewModal.subId);
    try {
      const result = await renewSubscription(dialerRenewModal.subId, dialerRenewModal.additionalDays);
      if (!result.success) throw new Error(result.error);
      setDialerRenewModal({ isOpen: false, subId: null, additionalDays: 30, currentExpiry: null, productId: DIALER_PRODUCT_IDS[0] });
      await loadDialerSubs();
    } catch (error: any) {
      alert(error.message ?? 'Failed to renew subscription');
    } finally {
      setDialerActionLoading(null);
    }
  };

  const handleDialerDelete = async (subId: string) => {
    if (!confirm('Are you sure you want to permanently delete this subscription? This cannot be undone.')) return;
    setDialerActionLoading(subId);
    try {
      const result = await deleteSubscription(subId);
      if (!result.success) throw new Error(result.error);
      await loadDialerSubs();
    } catch (error: any) {
      alert(error.message ?? 'Failed to delete subscription');
    } finally {
      setDialerActionLoading(null);
    }
  };

  const handleDialerChangeProduct = async () => {
    if (!dialerChangeProductModal.subId || dialerChangeProductModal.newProductId === dialerChangeProductModal.currentProductId) return;
    setDialerActionLoading(dialerChangeProductModal.subId);
    try {
      const pid = dialerChangeProductModal.newProductId;
      const result = await updateSubscriptionProduct(
        dialerChangeProductModal.subId,
        pid,
        DIALER_PRODUCT_NAMES[pid],
        DIALER_PRODUCT_PRICES[pid]
      );
      if (!result.success) throw new Error(result.error);
      setDialerChangeProductModal({ isOpen: false, subId: null, currentProductId: 0, newProductId: 0 });
      await loadDialerSubs();
    } catch (error: any) {
      alert(error.message ?? 'Failed to change product');
    } finally {
      setDialerActionLoading(null);
    }
  };

  const handleDialerSaveStates = async () => {
    if (!dialerEditStatesModal.orderId) {
      alert('No linked order found for this subscription. States are stored on the order.');
      return;
    }
    try {
      const { error } = await supabase
        .from('orders')
        .update({ states: dialerEditStatesModal.states.trim() })
        .eq('id', dialerEditStatesModal.orderId);
      if (error) throw error;
      setDialerEditStatesModal({ isOpen: false, subId: null, orderId: null, states: '' });
      await loadDialerSubs();
    } catch (error: any) {
      alert(error.message ?? 'Failed to update states');
    }
  };

  const handleBulkDialerDelete = async () => {
    if (selectedDialerSubs.size === 0) return;
    if (!confirm(`Are you sure you want to permanently delete ${selectedDialerSubs.size} subscription(s)? This cannot be undone.`)) return;
    setDialerActionLoading('bulk');
    try {
      const errors: string[] = [];
      for (const subId of selectedDialerSubs) {
        const result = await deleteSubscription(subId);
        if (!result.success) errors.push(result.error ?? subId);
      }
      if (errors.length > 0) alert('Some deletions failed:\n' + errors.join('\n'));
      setSelectedDialerSubs(new Set());
      await loadDialerSubs();
    } catch (error: any) {
      alert(error.message ?? 'Bulk delete failed');
    } finally {
      setDialerActionLoading(null);
    }
  };

  const handleBulkDialerExpire = async () => {
    if (selectedDialerSubs.size === 0) return;
    if (!confirm(`Expire ${selectedDialerSubs.size} selected subscription(s)?`)) return;
    setDialerActionLoading('bulk');
    try {
      const errors: string[] = [];
      for (const subId of selectedDialerSubs) {
        const result = await expireSubscription(subId);
        if (!result.success) errors.push(result.error ?? subId);
      }
      if (errors.length > 0) alert('Some expirations failed:\n' + errors.join('\n'));
      setSelectedDialerSubs(new Set());
      await loadDialerSubs();
    } catch (error: any) {
      alert(error.message ?? 'Bulk expire failed');
    } finally {
      setDialerActionLoading(null);
    }
  };

  const toggleDialerSubSelection = (subId: string) => {
    setSelectedDialerSubs(prev => {
      const next = new Set(prev);
      next.has(subId) ? next.delete(subId) : next.add(subId);
      return next;
    });
  };

  const toggleAllDialerSubs = (subIds: string[]) => {
    setSelectedDialerSubs(prev => {
      const allSelected = subIds.every(id => prev.has(id));
      if (allSelected) return new Set();
      return new Set(subIds);
    });
  };

  const getDaysSincePaid = (createdAt: string) => {
    const diff = Date.now() - new Date(createdAt).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  };

  const dialerStats = computeStats(dialerSubs);

  const filteredDialerSubs = dialerSubs.filter(s => {
    if (dialerFilter === 'free') {
      if (!s.is_free) return false;
    } else if (dialerFilter !== 'all' && s.status !== dialerFilter) {
      return false;
    }
    if (dialerSearch) {
      const q = dialerSearch.toLowerCase();
      if (!(s.agent_name ?? '').toLowerCase().includes(q) && !s.product_name.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const dialerStatusCounts: Record<string, number> = { all: dialerSubs.length };
  dialerSubs.forEach(s => { dialerStatusCounts[s.status] = (dialerStatusCounts[s.status] ?? 0) + 1; });
  const freeDialerCount = dialerSubs.filter(s => s.is_free).length;

  const formatDialerDate = (d: string | null) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/New_York' }) : '-';
  const formatDialerDateTime = (d: string | null) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York', timeZoneName: 'short' }) : '-';

  const dialerDaysColor = (d: number) => d <= 0 ? 'text-red-600' : d <= 3 ? 'text-red-500' : d <= 7 ? 'text-orange-500' : 'text-slate-700';

  const dialerStatusBadge: Record<string, string> = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    expiring_soon: 'bg-orange-50 text-orange-700 border-orange-200',
    expired: 'bg-red-50 text-red-600 border-red-200',
  };

  const dialerStatusLabel: Record<string, string> = {
    pending: 'Pending', active: 'Active', expiring_soon: 'Expiring Soon', expired: 'Expired',
  };

  const checkAdminAndLoadData = async () => {
    setIsLoading(true);
    try {
      const userResult = await getCurrentUser();
      if (!userResult.success || !userResult.user) {
        navigate('/agent/login');
        return;
      }

      const { data: agentData } = await supabase
        .from('agents')
        .select('role, first_name')
        .eq('user_id', userResult.user.id)
        .single();

      const userEmail = userResult.user.email?.toLowerCase();
      const overrideAdmin = isOverrideAdmin(userEmail);

      if (!overrideAdmin && (!agentData || agentData.role !== 'admin')) {
        setIsAdmin(false);
        setIsLoading(false);
        return;
      }

      setIsAdmin(true);
      setAdminName(agentData?.first_name || 'Owner');
      await loadAllData();
    } catch (error) {
      console.error('Error checking admin:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadAllData = async () => {
    try {
      const { data: rawAgentsData } = await supabase
        .from('agents')
        .select('*')
        .order('created_at', { ascending: false });

      // Hide owner-override accounts from every admin-visible listing
      const agentsData = (rawAgentsData || []).filter(
        (a: { email?: string | null }) => !isOverrideAdmin(a.email)
      );

      if (agentsData) {
        setAgents(agentsData);
        const admins = agentsData.filter(a => a.role === 'admin').length;
        const activeNonAdmins = agentsData.filter(a => a.status === 'active' && a.role !== 'admin').length;
        const disabledNonAdmins = agentsData.filter(a => a.status === 'disabled' && a.role !== 'admin').length;
        
        // Calculate new signups this week
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        const newThisWeek = agentsData.filter(a => new Date(a.created_at) >= weekAgo).length;
        
        setTotalAdmins(admins);
        setActiveAgents(activeNonAdmins);
        setDisabledAgents(disabledNonAdmins);
        setNewSignupsThisWeek(newThisWeek);
        setTotalAccounts(admins + activeNonAdmins);
      }

      // Fetch ALL orders to get pending count (paged past the 1000-row cap)
      const { data: allOrdersData } = await fetchAllRows<Order>(() =>
        supabase
          .from('orders')
          .select('*')
          .order('created_at', { ascending: false })
      );

      // Fetch only completed (paid) orders for revenue calculations
      const { data: ordersData } = await fetchAllRows<Order>(() =>
        supabase
          .from('orders')
          .select('*')
          .eq('status', 'completed')
          .order('created_at', { ascending: false })
      );

      if (allOrdersData) {
        const pending = allOrdersData.filter(o => o.status === 'pending').length;
        setPendingOrders(pending);
      }

      if (ordersData) {
        setOrders(ordersData);
        setTotalOrders(ordersData.length);
        
        // Calculate unfulfilled and fulfillment rate
        const unfulfilled = ordersData.filter(o => !o.fulfilled).length;
        const fulfillmentPct = ordersData.length > 0 ? ((ordersData.length - unfulfilled) / ordersData.length) * 100 : 0;
        setUnfulfilledOrders(unfulfilled);
        setFulfillmentRate(fulfillmentPct);
        
        // Calculate average order value
        const avgValue = ordersData.length > 0 
          ? ordersData.reduce((sum, o) => sum + (o.price || 0), 0) / ordersData.length 
          : 0;
        setAvgOrderValue(avgValue);
        
        calculateRevenue(ordersData);
        calculateDailySales(ordersData);
        calculateMonthlySales(ordersData);
        calculateProductBreakdown(ordersData);
        calculateYearlyMonthSales(ordersData);
      }

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadAllData();
    setTimeout(() => setIsRefreshing(false), 500);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      localStorage.removeItem('isAdmin');
      navigate('/agent/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const calculateRevenue = (ordersData: Order[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // Last 7 days (rolling)
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    const fourteenDaysAgo = new Date(today.getTime() - 14 * 24 * 60 * 60 * 1000);
    
    // Current week (Monday to Sunday)
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Adjust for Monday start
    const thisWeekStart = new Date(today.getTime() - daysFromMonday * 24 * 60 * 60 * 1000);
    const thisWeekEnd = new Date(thisWeekStart.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    // Previous week (Monday to Sunday)
    const prevWeekStart = new Date(thisWeekStart.getTime() - 7 * 24 * 60 * 60 * 1000);
    const prevWeekEnd = new Date(thisWeekStart.getTime());
    
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const yearStart = new Date(now.getFullYear(), 0, 1);

    let todayTotal = 0, todayCount = 0, last7DaysTotal = 0, thisWeekTotal = 0, 
        prevWeekTotal = 0, lastWeekLast7Days = 0, monthTotal = 0, ytdTotal = 0;

    ordersData.forEach(order => {
      // Use paid_at when available (matches fulfillment "yesterday's orders"); otherwise created_at
      const orderDate = new Date(order.paid_at || order.created_at);
      const price = order.price || 0;
      
      if (orderDate >= today) { todayTotal += price; todayCount++; }
      if (orderDate >= sevenDaysAgo) { last7DaysTotal += price; }
      if (orderDate >= fourteenDaysAgo && orderDate < sevenDaysAgo) { lastWeekLast7Days += price; }
      if (orderDate >= thisWeekStart && orderDate < thisWeekEnd) { thisWeekTotal += price; }
      if (orderDate >= prevWeekStart && orderDate < prevWeekEnd) { prevWeekTotal += price; }
      if (orderDate >= monthStart) monthTotal += price;
      if (orderDate >= yearStart) ytdTotal += price;
    });

    setTodayRevenue(todayTotal);
    setTodayOrders(todayCount);
    setLast7DaysRevenue(last7DaysTotal);
    setThisWeekRevenue(thisWeekTotal);
    setLastWeekRevenue(lastWeekLast7Days);
    setPrevWeekRevenue(prevWeekTotal);
    setMonthRevenue(monthTotal);
    setYtdRevenue(ytdTotal);
  };

  const calculateDailySales = (ordersData: Order[]) => {
    const last14Days: DailySales[] = [];
    const now = new Date();

    for (let i = 13; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      
      const dayOrders = ordersData.filter(o => {
        const orderDate = new Date(o.paid_at || o.created_at);
        const orderDateStr = `${orderDate.getFullYear()}-${String(orderDate.getMonth() + 1).padStart(2, '0')}-${String(orderDate.getDate()).padStart(2, '0')}`;
        return orderDateStr === dateStr;
      });
      
      last14Days.push({
        date: dateStr,
        total: dayOrders.reduce((sum, o) => sum + (o.price || 0), 0),
        count: dayOrders.length
      });
    }

    setDailySales(last14Days);
  };

  const calculateMonthlySales = (ordersData: Order[]) => {
    const last30Days: DailySales[] = [];
    const now = new Date();

    for (let i = 29; i >= 0; i--) {
      const date = new Date(now.getTime() - i * 24 * 60 * 60 * 1000);
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
      
      const dayOrders = ordersData.filter(o => {
        const orderDate = new Date(o.paid_at || o.created_at);
        const orderDateStr = `${orderDate.getFullYear()}-${String(orderDate.getMonth() + 1).padStart(2, '0')}-${String(orderDate.getDate()).padStart(2, '0')}`;
        return orderDateStr === dateStr;
      });
      
      last30Days.push({
        date: dateStr,
        total: dayOrders.reduce((sum, o) => sum + (o.price || 0), 0),
        count: dayOrders.length
      });
    }

    setMonthlySales(last30Days);
  };

  const calculateYearlyMonthSales = (ordersData: Order[]) => {
    const months: DailySales[] = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = d.getFullYear();
      const month = d.getMonth();
      const label = d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
      const monthOrders = ordersData.filter(o => {
        const od = new Date(o.created_at);
        return od.getFullYear() === year && od.getMonth() === month;
      });
      months.push({
        date: label,
        total: monthOrders.reduce((sum, o) => sum + (o.price || 0), 0),
        count: monthOrders.length,
      });
    }
    setYearlyMonthSales(months);
  };

  const calculateProductBreakdown = (ordersData: Order[]) => {
    const breakdown: Record<string, {count: number; revenue: number}> = {};

    ordersData.forEach(order => {
      const name = order.product_name || 'Unknown';
      if (!breakdown[name]) breakdown[name] = { count: 0, revenue: 0 };
      breakdown[name].count++;
      breakdown[name].revenue += order.price || 0;
    });

    const sorted = Object.entries(breakdown)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue);

    setProductBreakdown(sorted);
    
    // Set top product
    if (sorted.length > 0) {
      setTopProduct(sorted[0].name);
    }
  };

  const toggleAgentStatus = async (agent: Agent) => {
    const newStatus = agent.status === 'active' ? 'disabled' : 'active';
    
    // Select back the updated row: a Supabase update that matches no rows (e.g.
    // blocked by RLS) returns success with no error, which would leave the UI
    // showing a status change that never persisted.
    const { data, error } = await supabase
      .from('agents')
      .update({ status: newStatus })
      .eq('id', agent.id)
      .select('id, status');

    if (error) {
      alert(`Failed to update status: ${error.message}`);
      return;
    }

    if (!data || data.length === 0) {
      alert('Failed to update status: the change was not saved. You may not have permission to modify this account.');
      return;
    }

    setAgents(prev => prev.map(a => a.id === agent.id ? { ...a, status: newStatus } : a));
    
    // Only update activeAgents count for non-admin agents
    if (agent.role !== 'admin') {
      setActiveAgents(prev => newStatus === 'active' ? prev + 1 : prev - 1);
    }
  };

  const handleCreateAgent = async () => {
    const { firstName, lastName, email, phone, password, confirmPassword } = createAgentModal;

    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password) {
      setCreateAgentModal(prev => ({ ...prev, error: 'Please fill in all required fields.' }));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setCreateAgentModal(prev => ({ ...prev, error: 'Please enter a valid email address.' }));
      return;
    }
    if (password.length < 6) {
      setCreateAgentModal(prev => ({ ...prev, error: 'Password must be at least 6 characters.' }));
      return;
    }
    if (password !== confirmPassword) {
      setCreateAgentModal(prev => ({ ...prev, error: 'Passwords do not match.' }));
      return;
    }

    setCreateAgentModal(prev => ({ ...prev, isSubmitting: true, error: null }));

    const result = await createAgentAccount({ firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim(), phone: phone.trim(), password });

    if (result.success) {
      setCreateAgentModal(prev => ({
        ...prev,
        isSubmitting: false,
        success: `Account created${result.emailSent ? ` and credentials emailed to ${result.agentEmail}` : ` for ${result.agentEmail} (email not sent, share credentials manually)`}`,
      }));
      loadAllData();
    } else {
      setCreateAgentModal(prev => ({ ...prev, isSubmitting: false, error: result.error || 'Failed to create account.' }));
    }
  };

  const resetCreateAgentModal = () => {
    setCreateAgentModal({
      isOpen: false, firstName: '', lastName: '', email: '', phone: '',
      password: '', confirmPassword: '', showPassword: false,
      isSubmitting: false, error: null, success: null,
    });
  };

  const openFulfillmentModal = (order: Order, editMode: boolean = false) => {
    // Open modal to add/edit Google Sheet URL
    setFulfillmentModal({
      isOpen: true,
      order,
      googleSheetUrl: order.google_sheet_url || ''
    });
  };

  const closeFulfillmentModal = () => {
    setFulfillmentModal({ isOpen: false, order: null, googleSheetUrl: '' });
  };

  const isLeadOrder = (order: Order) =>
    order.product_category !== 'Dialer Technology' && !order.product_name?.toLowerCase().includes('dialer');

  const fulfillOrderWithSheet = async () => {
    const modalOrder = fulfillmentModal.order;
    if (!modalOrder) return;

    // Get the current order state from the orders array (updated by real-time subscriptions)
    // to avoid stale closure issues
    const currentOrder = orders.find(o => o.id === modalOrder.id);
    if (!currentOrder) {
      alert('Order not found. It may have been deleted.');
      closeFulfillmentModal();
      return;
    }

    const now = new Date().toISOString();
    const googleSheetUrl = fulfillmentModal.googleSheetUrl.trim();

    // --- LEAD ORDERS: send to Make.com webhook, wait for callback ---
    if (isLeadOrder(currentOrder) && !currentOrder.fulfilled) {
      // Save the Google Sheet URL and mark as pending fulfillment
      const { error: saveError } = await supabase
        .from('orders')
        .update({
          google_sheet_url: googleSheetUrl || null,
          notes: (currentOrder.notes || '') + ` [FULFILLMENT_PENDING ${now}]`
        })
        .eq('id', modalOrder.id);

      if (saveError) {
        alert(`Failed to save order: ${saveError.message}`);
        return;
      }

      // Look up agent info
      const agent = agents.find(a => a.user_id === currentOrder.agent_id);

      // Send all order information to Make.com webhook
      const webhookPayload = {
        orderId: currentOrder.id,
        status: currentOrder.status,
        productName: currentOrder.product_name,
        productCategory: currentOrder.product_category,
        quantity: currentOrder.quantity,
        price: currentOrder.price,
        states: currentOrder.states || [],
        customerName: currentOrder.customer_name || '',
        customerEmail: currentOrder.customer_email || '',
        customerPhone: currentOrder.customer_phone || '',
        googleSheetUrl: googleSheetUrl || '',
        shopifyOrderId: currentOrder.shopify_order_id || '',
        notes: currentOrder.notes || '',
        paidAt: currentOrder.paid_at || '',
        createdAt: currentOrder.created_at,
        agentId: currentOrder.agent_id,
        agentFirstName: agent?.first_name || '',
        agentLastName: agent?.last_name || '',
        agentEmail: agent?.email || '',
        agentPhone: agent?.phone || '',
        agentRole: agent?.role || '',
        fulfilledBy: adminName,
        sentAt: now,
      };

      try {
        const response = await fetch('https://hook.us2.make.com/w938c2gmn2xt6qtopuq5ppyeyigxi3re', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(webhookPayload),
        });

        if (!response.ok) {
          alert('Failed to send to fulfillment webhook. Please try again.');
          return;
        }

        console.log('✅ Order sent to Make.com fulfillment webhook, awaiting callback');
      } catch (err) {
        console.error('❌ Webhook send failed:', err);
        alert('Failed to send to fulfillment webhook. Please try again.');
        return;
      }

      // Update local state to show pending
      setOrders(prev => prev.map(o =>
        o.id === modalOrder.id ? {
          ...o,
          google_sheet_url: googleSheetUrl || null,
          notes: (o.notes || '') + ` [FULFILLMENT_PENDING ${now}]`
        } : o
      ));

      closeFulfillmentModal();
      return;
    }

    // --- DIALER ORDERS or already-fulfilled edits: immediate fulfillment (existing behavior) ---
    // Preserve the original fulfilled_at if order is already fulfilled
    const fulfilledAt = currentOrder.fulfilled && currentOrder.fulfilled_at
      ? currentOrder.fulfilled_at
      : now;

    const updateData: Record<string, any> = {
      fulfilled: true,
      fulfilled_at: fulfilledAt,
      google_sheet_url: googleSheetUrl || null
    };

    const { error } = await supabase
      .from('orders')
      .update(updateData)
      .eq('id', modalOrder.id);

    if (error) {
      alert(`Failed to update order: ${error.message}`);
      return;
    }

    setOrders(prev => prev.map(o =>
      o.id === modalOrder.id ? {
        ...o,
        fulfilled: true,
        fulfilled_at: fulfilledAt,
        google_sheet_url: googleSheetUrl || null
      } : o
    ));

    // Send fulfillment notification email if Google Sheet URL is provided
    // Only send for new fulfillments or when URL is updated
    if (googleSheetUrl && (!currentOrder.google_sheet_url || currentOrder.google_sheet_url !== googleSheetUrl)) {
      try {
        console.log('📧 Sending fulfillment email notification...');
        const { error: emailError } = await supabase.functions.invoke('send-fulfillment-email', {
          body: { orderId: modalOrder.id, googleSheetUrl }
        });

        if (emailError) {
          console.error('⚠️ Failed to send fulfillment email (non-critical):', emailError);
        } else {
          console.log('✅ Fulfillment email sent successfully');
        }
      } catch (emailErr) {
        console.error('⚠️ Email notification failed (non-critical):', emailErr);
      }
    }

    closeFulfillmentModal();
  };

  const unfulfillOrder = async (order: Order) => {
    if (!confirm('Are you sure you want to unfulfill this order? This will remove the Google Sheet URL.')) {
      return;
    }
    
    const { error } = await supabase
      .from('orders')
      .update({ fulfilled: false, fulfilled_at: null, google_sheet_url: null })
      .eq('id', order.id);

    if (error) {
      alert(`Failed to update order: ${error.message}`);
      return;
    }

    setOrders(prev => prev.map(o => 
      o.id === order.id ? { ...o, fulfilled: false, fulfilled_at: null, google_sheet_url: null } : o
    ));
  };

  const openManualOrderModal = () => {
    setManualOrderModal({
      isOpen: true,
      editMode: false,
      editOrderId: null,
      agentId: '',
      productCategory: 'Fresh Leads',
      selectedProductId: null,
      selectedPackageIndex: null,
      customProductName: '',
      customQuantity: 0,
      customPrice: 0,
      useCustom: false,
      googleSheetUrl: '',
      states: [],
      notes: '',
      showStatesDropdown: false
    });
  };

  const openEditOrderModal = (order: Order) => {
    console.log('Editing order:', order);
    
    // Find matching product
    const allProducts = [...products, ...agedProducts, ...smsProducts];
    const matchingProduct = allProducts.find(p => p.name === order.product_name);
    
    console.log('Found matching product:', matchingProduct);
    
    let category: 'Fresh Leads' | 'Aged Leads' | 'SMS Leads' | 'Dialer Technology' = 'Fresh Leads';
    if (order.product_category.includes('Aged')) category = 'Aged Leads';
    else if (order.product_category.includes('SMS')) category = 'SMS Leads';
    else if (order.product_category.includes('Dialer')) category = 'Dialer Technology';
    
    // Find matching package
    let packageIndex = null;
    let useCustom = false;
    
    if (matchingProduct) {
      packageIndex = matchingProduct.pricing.packages.findIndex(
        pkg => pkg.quantity === order.quantity && pkg.price === order.price
      );
      console.log('Package index found:', packageIndex, 'for quantity:', order.quantity, 'price:', order.price);
      
      // If no matching package found, use custom
      if (packageIndex === -1) {
        useCustom = true;
      }
    } else {
      // Product not in catalog, use custom
      useCustom = true;
    }
    
    setManualOrderModal({
      isOpen: true,
      editMode: true,
      editOrderId: order.id,
      agentId: order.agent_id,
      productCategory: category,
      selectedProductId: matchingProduct?.id || null,
      selectedPackageIndex: packageIndex !== -1 ? packageIndex : null,
      customProductName: order.product_name || '',
      customQuantity: order.quantity || 0,
      customPrice: order.price || 0,
      useCustom: useCustom,
      googleSheetUrl: order.google_sheet_url || '',
      states: Array.isArray(order.states) ? order.states : [],
      notes: order.notes || '',
      showStatesDropdown: false
    });
  };

  const closeManualOrderModal = () => {
    setManualOrderModal({
      isOpen: false,
      editMode: false,
      editOrderId: null,
      agentId: '',
      productCategory: 'Fresh Leads',
      selectedProductId: null,
      selectedPackageIndex: null,
      customProductName: '',
      customQuantity: 0,
      customPrice: 0,
      useCustom: false,
      googleSheetUrl: '',
      states: [],
      notes: '',
      showStatesDropdown: false
    });
  };

  // Get products based on selected category
  const getAvailableProducts = () => {
    const allProducts = [...products, ...agedProducts, ...smsProducts];
    
    switch (manualOrderModal.productCategory) {
      case 'Fresh Leads':
        return products.filter(p => p.category !== 'Dialer Technology');
      case 'Aged Leads':
        return agedProducts;
      case 'SMS Leads':
        return smsProducts;
      case 'Dialer Technology':
        return products.filter(p => p.category === 'Dialer Technology');
      default:
        return [];
    }
  };

  // Get selected product details
  const getSelectedProduct = () => {
    const allProducts = [...products, ...agedProducts, ...smsProducts];
    return allProducts.find(p => p.id === manualOrderModal.selectedProductId);
  };

  // Get current order details
  const getCurrentOrderDetails = () => {
    if (manualOrderModal.useCustom) {
      return {
        productName: manualOrderModal.customProductName,
        quantity: manualOrderModal.customQuantity,
        price: manualOrderModal.customPrice
      };
    }
    
    const product = getSelectedProduct();
    if (!product || manualOrderModal.selectedPackageIndex === null) {
      return { productName: '', quantity: 0, price: 0 };
    }
    
    const pkg = product.pricing.packages[manualOrderModal.selectedPackageIndex];
    return {
      productName: product.name,
      quantity: pkg.quantity,
      price: pkg.price
    };
  };

  const submitManualOrder = async () => {
    const orderDetails = getCurrentOrderDetails();
    
    // Validate required fields
    if (!manualOrderModal.agentId) {
      alert('Please select an agent');
      return;
    }
    
    if (manualOrderModal.useCustom) {
      // Validate custom entry
      if (!manualOrderModal.customProductName) {
        alert('Please enter a product name');
        return;
      }
      if (manualOrderModal.customQuantity <= 0) {
        alert('Please enter a valid quantity');
        return;
      }
      if (manualOrderModal.customPrice <= 0) {
        alert('Please enter a valid price');
        return;
      }
    } else {
      // Validate catalog selection
      if (!manualOrderModal.selectedProductId) {
        alert('Please select a product');
        return;
      }
      if (manualOrderModal.selectedPackageIndex === null) {
        alert('Please select a quantity package');
        return;
      }
    }

    // Get agent details
    const selectedAgent = agents.find(a => a.user_id === manualOrderModal.agentId);
    if (!selectedAgent) {
      alert('Selected agent not found');
      return;
    }

    const now = new Date().toISOString();
    
    if (manualOrderModal.editMode && manualOrderModal.editOrderId) {
      // UPDATE existing order
      const orderData = {
        agent_id: manualOrderModal.agentId,
        product_name: orderDetails.productName,
        product_category: manualOrderModal.productCategory,
        quantity: orderDetails.quantity,
        price: orderDetails.price,
        customer_name: `${selectedAgent.first_name} ${selectedAgent.last_name}`,
        customer_email: selectedAgent.email,
        customer_phone: selectedAgent.phone,
        states: manualOrderModal.states.length > 0 ? manualOrderModal.states : null,
        notes: manualOrderModal.notes || null,
        fulfilled: manualOrderModal.googleSheetUrl ? true : false,
        fulfilled_at: manualOrderModal.googleSheetUrl ? now : null,
        google_sheet_url: manualOrderModal.googleSheetUrl || null,
      };

      const { error } = await supabase
        .from('orders')
        .update(orderData)
        .eq('id', manualOrderModal.editOrderId);

      if (error) {
        alert(`Failed to update order: ${error.message}`);
        console.error('Order update error:', error);
        return;
      }

      // Send fulfillment email if Google Sheet URL was just added
      if (manualOrderModal.googleSheetUrl) {
        try {
          await supabase.functions.invoke('send-fulfillment-email', {
            body: { orderId: manualOrderModal.editOrderId, googleSheetUrl: manualOrderModal.googleSheetUrl }
          });
        } catch (emailErr) {
          console.error('Email notification failed (non-critical):', emailErr);
        }
      }

      alert('Order updated successfully!');
    } else {
      // CREATE new order
      const orderData = {
        agent_id: manualOrderModal.agentId,
        product_name: orderDetails.productName,
        product_category: manualOrderModal.productCategory,
        quantity: orderDetails.quantity,
        price: orderDetails.price,
        status: 'completed', // Manual orders are already paid
        created_at: now,
        paid_at: now,
        customer_name: `${selectedAgent.first_name} ${selectedAgent.last_name}`,
        customer_email: selectedAgent.email,
        customer_phone: selectedAgent.phone,
        states: manualOrderModal.states.length > 0 ? manualOrderModal.states : null,
        notes: manualOrderModal.notes || null,
        fulfilled: manualOrderModal.googleSheetUrl ? true : false,
        fulfilled_at: manualOrderModal.googleSheetUrl ? now : null,
        google_sheet_url: manualOrderModal.googleSheetUrl || null,
        shopify_order_id: null // Manual orders don't have Shopify IDs
      };

      const { data, error } = await supabase
        .from('orders')
        .insert([orderData])
        .select()
        .single();

      if (error) {
        alert(`Failed to create order: ${error.message}`);
        console.error('Order creation error:', error);
        return;
      }

      // Send fulfillment email if Google Sheet URL is provided
      if (manualOrderModal.googleSheetUrl && data) {
        try {
          await supabase.functions.invoke('send-fulfillment-email', {
            body: { orderId: data.id, googleSheetUrl: manualOrderModal.googleSheetUrl }
          });
        } catch (emailErr) {
          console.error('Email notification failed (non-critical):', emailErr);
        }
      }

      alert('Manual order created successfully!');
    }

    // Refresh data
    await loadAllData();
    
    // Close modal
    closeManualOrderModal();
  };

  const deleteManualOrder = async (order: Order) => {
    if (!confirm(`Are you sure you want to delete this order?\n\nProduct: ${order.product_name}\nQuantity: ${order.quantity}\nPrice: $${order.price}\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('orders')
        .update({ status: 'cancelled' })
        .eq('id', order.id);

      if (error) {
        alert(`Failed to delete order: ${error.message}`);
        console.error('Order deletion error:', error);
        return;
      }

      await loadAllData();
    } catch (err) {
      console.error('Unexpected delete error:', err);
      alert('An unexpected error occurred while deleting the order.');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getAvatarColor = (name: string) => {
    const colors = [
      'bg-blue-500',
      'bg-emerald-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-orange-500',
      'bg-teal-500',
      'bg-cyan-500'
    ];
    const index = (name.charCodeAt(0) + name.charCodeAt(name.length - 1)) % colors.length;
    return colors[index];
  };

  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  };

  const filteredAgents = agents.filter(agent => 
    agent.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.last_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exportActiveAgentsCsv = () => {
    const rows = agents
      .filter(a => a.status === 'active' && a.role !== 'admin')
      .sort((a, b) => `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(`${b.first_name || ''} ${b.last_name || ''}`))
      .map(a => [
        a.first_name || '',
        a.last_name || '',
        a.email || '',
        a.phone || '',
        a.state || '',
        a.created_at ? new Date(a.created_at).toISOString().split('T')[0] : '',
      ]);
    const headers = ['First Name', 'Last Name', 'Email', 'Phone', 'State', 'Signup Date'];
    const escape = (c: string) => {
      const text = String(c);
      // Neutralize spreadsheet formula prefixes so agent-typed values never execute in Excel
      const safe = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
      return `"${safe.replace(/"/g, '""')}"`;
    };
    const csv = [headers, ...rows].map(r => r.map(escape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `active-agents-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const adminUsers = filteredAgents.filter(a => a.role === 'admin');
  const regularAgents = filteredAgents.filter(a => a.role !== 'admin');
  const maxDailySale = Math.max(...dailySales.map(d => d.total), 1);
  const maxMonthlySale = Math.max(...monthlySales.map(d => d.total), 1);
  const maxYearlyMonth = Math.max(...yearlyMonthSales.map(d => d.total), 1);

  // ============================================
  // API KEY MANAGEMENT FUNCTIONS
  // ============================================
  const loadApiKeys = async () => {
    setApiKeysLoading(true);
    try {
      const result = await getApiKeys();
      if (result.success && result.data) {
        setApiKeys(result.data);
      }
    } catch (err) {
      console.error('Failed to load API keys:', err);
    } finally {
      setApiKeysLoading(false);
    }
  };

  const handleGenerateKey = async () => {
    if (!newKeyName.trim()) return;
    const result = await generateApiKey(newKeyName.trim());
    if (result.success && result.apiKey) {
      setNewlyCreatedKey(result.apiKey);
      if (result.recoveryCode) {
        setNewlyCreatedRecoveryCode(result.recoveryCode);
      }
      setNewKeyName('');
      loadApiKeys();
    } else {
      alert(result.error || 'Failed to generate API key.');
    }
  };

  const handleRevokeKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to revoke this API key? Any integrations using it will stop working.')) return;
    const result = await revokeApiKey(keyId);
    if (result.success) {
      loadApiKeys();
    } else {
      alert(result.error || 'Failed to revoke key.');
    }
  };

  const handleRotateKey = async () => {
    if (!rotatePassword) {
      setRotateError('Password is required.');
      return;
    }
    setRotateError('');
    const result = await rotateApiKey(showRotateModal.keyId, rotatePassword, `${showRotateModal.keyName} (rotated)`);
    if (result.success && result.apiKey) {
      setNewlyCreatedKey(result.apiKey);
      if (result.recoveryCode) {
        setNewlyCreatedRecoveryCode(result.recoveryCode);
      }
      setShowRotateModal({ isOpen: false, keyId: '', keyName: '' });
      setRotatePassword('');
      setShowNewKeyModal(true);
      loadApiKeys();
    } else {
      setRotateError(result.error || 'Failed to rotate key.');
    }
  };

  const handleViewLogs = async (keyId: string, keyName: string) => {
    const result = await getApiKeyUsageLogs(keyId);
    if (result.success && result.data) {
      setSelectedKeyLogs({ keyId, keyName, logs: result.data });
    }
  };

  const copyToClipboard = async (text: string, type: 'key' | 'recovery') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'key') {
        setKeyCopied(true);
        setTimeout(() => setKeyCopied(false), 2000);
      } else {
        setRecoveryCopied(true);
        setTimeout(() => setRecoveryCopied(false), 2000);
      }
    } catch {
      // Fallback
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      if (type === 'key') {
        setKeyCopied(true);
        setTimeout(() => setKeyCopied(false), 2000);
      } else {
        setRecoveryCopied(true);
        setTimeout(() => setRecoveryCopied(false), 2000);
      }
    }
  };

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const getSupabaseEndpoint = () => {
    const url = import.meta.env.VITE_SUPABASE_URL;
    return url ? `${url}/functions/v1/fulfill-order` : 'https://your-project.supabase.co/functions/v1/fulfill-order';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center px-6">
          <img
            src="/insurance logos/EC logo transparent main.png"
            alt="Enhance Companies"
            className="h-12 mx-auto object-contain mb-8 opacity-80"
          />
          <div className="w-5 h-5 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <img src="/insurance logos/EC logo transparent main.png" alt="Enhance Companies" className="h-12 mx-auto object-contain mb-8 opacity-80" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-sm text-slate-400 mb-6">You don't have permission to access the admin panel.</p>
          <button
            onClick={() => navigate('/agent/dashboard')}
            className="px-5 py-2 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 transition-colors font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header onLogout={handleLogout} />

      {/* Spacer for fixed header */}
      <div className="h-16"></div>

      {/* Tabs */}
      <div className="border-b border-slate-200 bg-white overflow-x-auto sticky top-16 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-2">
          <nav className="flex gap-4 sm:gap-6 min-w-max">
            {[
              { id: 'overview', label: 'Overview' },
              { id: 'users', label: 'Agents' },
              { id: 'orders', label: 'Orders' },
              { id: 'store-orders', label: 'Store Orders' },
              { id: 'inbound-inquiries', label: 'Inbound Inquiries' },
              { id: 'dialer-subs', label: 'Dialer Subs' },
              { id: 'products', label: 'Products' },
              { id: 'api-keys', label: 'API Keys' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-3 sm:py-4 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-navy-600 border-navy-600'
                    : 'text-slate-500 border-transparent hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 page-transition">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div>
            {/* Top Row: Revenue headline + refresh */}
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Total Revenue</p>
                <p className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">{formatCurrency(ytdRevenue)}</p>
                <p className="text-sm text-slate-400 mt-1">Year to date</p>
              </div>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="text-xs text-slate-400 hover:text-slate-600 transition-colors pb-1"
              >
                {isRefreshing ? 'Refreshing...' : `Updated ${lastUpdated ? lastUpdated.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '-'}`}
              </button>
            </div>

            {/* Revenue Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-200 rounded-lg overflow-hidden mb-8">
              {[
                { label: 'Today', value: todayRevenue, sub: `${todayOrders} order${todayOrders !== 1 ? 's' : ''}` },
                { label: 'This Week', value: thisWeekRevenue, sub: prevWeekRevenue > 0 ? `${calculatePercentageChange(thisWeekRevenue, prevWeekRevenue) >= 0 ? '+' : ''}${calculatePercentageChange(thisWeekRevenue, prevWeekRevenue).toFixed(1)}% vs last` : 'Mon – Sun' },
                { label: 'Last 7 Days', value: last7DaysRevenue, sub: lastWeekRevenue > 0 ? `${calculatePercentageChange(last7DaysRevenue, lastWeekRevenue) >= 0 ? '+' : ''}${calculatePercentageChange(last7DaysRevenue, lastWeekRevenue).toFixed(1)}% vs prior` : 'Rolling' },
                { label: 'This Month', value: monthRevenue, sub: new Date().toLocaleDateString('en-US', { month: 'long' }) },
              ].map((item) => (
                <div key={item.label} className="bg-white p-4 sm:p-5">
                  <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">{item.label}</p>
                  <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">{formatCurrency(item.value)}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{item.sub}</p>
                </div>
              ))}
            </div>

            {/* Two-column: Accounts + Orders */}
            <div className="grid lg:grid-cols-2 gap-6 mb-8">
              {/* Accounts */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Accounts</h3>
                </div>
                <table className="w-full">
                  <tbody className="divide-y divide-slate-100">
                    {[
                      { label: 'Total Accounts', value: totalAccounts },
                      { label: 'Admins', value: totalAdmins },
                      { label: 'Active Agents', value: activeAgents },
                      { label: 'Disabled', value: disabledAgents },
                      { label: 'New This Week', value: newSignupsThisWeek, highlight: true },
                    ].map((row) => (
                      <tr key={row.label}>
                        <td className="px-5 py-3 text-sm text-slate-600">{row.label}</td>
                        <td className={`px-5 py-3 text-sm font-semibold text-right ${row.highlight ? 'text-navy-600' : 'text-slate-900'}`}>
                          {row.highlight ? `+${row.value}` : row.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Orders */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Orders</h3>
                </div>
                <table className="w-full">
                  <tbody className="divide-y divide-slate-100">
                    {[
                      { label: 'Total Paid', value: totalOrders },
                      { label: 'Today', value: todayOrders },
                      { label: 'Unfulfilled', value: unfulfilledOrders, warn: unfulfilledOrders > 0 },
                      { label: 'Unpaid (never paid)', value: pendingOrders, warn: pendingOrders > 0 },
                      { label: 'Fulfillment Rate', value: `${fulfillmentRate.toFixed(0)}%` },
                    ].map((row) => (
                      <tr key={row.label}>
                        <td className="px-5 py-3 text-sm text-slate-600">{row.label}</td>
                        <td className={`px-5 py-3 text-sm font-semibold text-right ${row.warn ? 'text-red-500' : 'text-slate-900'}`}>
                          {row.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Performance row */}
            <div className="grid grid-cols-3 gap-px bg-slate-200 rounded-lg overflow-hidden mb-8">
              <div className="bg-white p-4 sm:p-5">
                <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">Avg Order Value</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">{formatCurrency(avgOrderValue)}</p>
              </div>
              <div className="bg-white p-4 sm:p-5">
                <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">Top Product</p>
                <p className="text-sm sm:text-base font-bold text-slate-900 mt-2 truncate" title={topProduct}>{topProduct || '-'}</p>
              </div>
              <div className="bg-white p-4 sm:p-5">
                <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">Active Dialer Subs</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">{dialerStats.active}</p>
              </div>
            </div>

            {/* Product Breakdown */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-200 rounded-lg overflow-hidden mb-8">
              {[
                { label: 'Fresh Leads', orders: orders.filter(o => !o.product_name?.toLowerCase().includes('aged') && !o.product_name?.toLowerCase().includes('dialer') && !o.product_name?.toLowerCase().includes('sms') && !o.product_category?.includes('SMS')) },
                { label: 'Aged Leads', orders: orders.filter(o => o.product_name?.toLowerCase().includes('aged')) },
                { label: 'SMS Leads', orders: orders.filter(o => o.product_name?.toLowerCase().includes('sms') || o.product_category?.includes('SMS')) },
                { label: 'Dialer Products', orders: orders.filter(o => o.product_name?.toLowerCase().includes('dialer')) }
              ].map(({ label, orders: typeOrders }) => (
                <div key={label} className="bg-white p-4 sm:p-5">
                  <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">{label}</p>
                  <p className="text-xl sm:text-2xl font-bold text-slate-900 mt-1">
                    {formatCurrency(typeOrders.reduce((sum, o) => sum + (o.price || 0), 0))}
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1">{typeOrders.length} orders</p>
                </div>
              ))}
            </div>

            {/* 14-Day Revenue Chart */}
            <div className="border border-slate-200 rounded-lg overflow-hidden mb-8">
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Last 14 Days</h3>
                <span className="text-sm font-semibold text-slate-900">{formatCurrency(dailySales.reduce((sum, d) => sum + d.total, 0))}</span>
              </div>
              <div className="p-5">
                <div className="flex">
                  <div className="flex flex-col justify-between h-40 sm:h-48 pr-3 text-right flex-shrink-0 w-12 sm:w-14">
                    <span className="text-[10px] text-slate-400">{formatCurrency(maxDailySale)}</span>
                    <span className="text-[10px] text-slate-400">{formatCurrency(maxDailySale / 2)}</span>
                    <span className="text-[10px] text-slate-400">$0</span>
                  </div>
                  <div className="flex-1 flex items-end gap-1 sm:gap-1.5 h-40 sm:h-48 border-l border-b border-slate-100 pl-2">
                    {dailySales.map((day, i) => {
                      const heightPercent = maxDailySale > 0 ? Math.min((day.total / maxDailySale) * 100, 100) : 0;
                      const hasRevenue = day.total > 0;
                      const dateLabel = new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      return (
                        <div key={i} className="flex-1 group cursor-pointer relative h-full flex flex-col justify-end">
                          <div
                            className={`w-full rounded-t-sm transition-colors ${hasRevenue ? 'bg-navy-600 group-hover:bg-navy-500' : 'bg-slate-100'}`}
                            style={{ height: hasRevenue ? `${Math.max(heightPercent, 2)}%` : '1px' }}
                          >
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              <div className="bg-slate-900 text-white text-[10px] px-2 py-1.5 rounded whitespace-nowrap">
                                <p className="text-slate-400">{dateLabel}</p>
                                <p className="font-semibold">{formatCurrency(day.total)}</p>
                                <p className="text-slate-400">{day.count} order{day.count !== 1 ? 's' : ''}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex ml-14 sm:ml-16 mt-1.5">
                  {dailySales.map((day, i) => (
                    <div key={i} className="flex-1 text-center">
                      <span className="text-[9px] text-slate-400">
                        {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { day: 'numeric' })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 30-Day Revenue Chart */}
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Last 30 Days</h3>
                <span className="text-sm font-semibold text-slate-900">{formatCurrency(monthlySales.reduce((sum, d) => sum + d.total, 0))}</span>
              </div>
              <div className="p-5">
                <div className="flex">
                  <div className="flex flex-col justify-between h-40 sm:h-48 pr-2 sm:pr-3 text-right flex-shrink-0 w-12 sm:w-14">
                    <span className="text-[10px] text-slate-400">{formatCurrency(maxMonthlySale)}</span>
                    <span className="text-[10px] text-slate-400">{formatCurrency(maxMonthlySale / 2)}</span>
                    <span className="text-[10px] text-slate-400">$0</span>
                  </div>
                  <div className="flex-1 flex items-end gap-[2px] sm:gap-[3px] h-40 sm:h-48 border-l border-b border-slate-100 pl-1">
                    {monthlySales.map((day, i) => {
                      const heightPercent = maxMonthlySale > 0 ? Math.min((day.total / maxMonthlySale) * 100, 100) : 0;
                      const hasRevenue = day.total > 0;
                      const dateLabel = new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      return (
                        <div key={i} className="relative flex-1 group" style={{ height: '100%' }}>
                          <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1.5 bg-slate-900 text-white text-[10px] rounded opacity-0 group-hover:opacity-100 transition-opacity z-10 whitespace-nowrap pointer-events-none">
                            <p className="text-slate-400">{dateLabel}</p>
                            <p className="font-semibold">{formatCurrency(day.total)}</p>
                            <p className="text-slate-400">{day.count} order{day.count !== 1 ? 's' : ''}</p>
                          </div>
                          <div
                            className={`absolute bottom-0 left-0 right-0 rounded-t-sm transition-colors ${hasRevenue ? 'bg-slate-800 group-hover:bg-slate-600' : 'bg-slate-100'}`}
                            style={{ height: hasRevenue ? `${Math.max(heightPercent, 2)}%` : '1px' }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex ml-14 sm:ml-16 mt-1.5">
                  {monthlySales.map((day, i) => (
                    <div key={i} className="flex-1 text-center">
                      {i % 5 === 0 || i === 29 ? (
                        <span className="text-[8px] sm:text-[9px] text-slate-400">
                          {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* 12-Month Revenue Chart */}
            <div className="border border-slate-200 rounded-lg overflow-hidden mt-8">
              <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Last 12 Months</h3>
                <span className="text-sm font-semibold text-slate-900">{formatCurrency(yearlyMonthSales.reduce((sum, d) => sum + d.total, 0))}</span>
              </div>
              <div className="p-5">
                <div className="flex">
                  <div className="flex flex-col justify-between h-48 sm:h-56 pr-2 sm:pr-3 text-right flex-shrink-0 w-12 sm:w-14">
                    <span className="text-[10px] text-slate-400">{formatCurrency(maxYearlyMonth)}</span>
                    <span className="text-[10px] text-slate-400">{formatCurrency(maxYearlyMonth / 2)}</span>
                    <span className="text-[10px] text-slate-400">$0</span>
                  </div>
                  <div className="flex-1 flex items-end gap-1 sm:gap-2 h-48 sm:h-56 border-l border-b border-slate-100 pl-2">
                    {yearlyMonthSales.map((month, i) => {
                      const heightPercent = maxYearlyMonth > 0 ? Math.min((month.total / maxYearlyMonth) * 100, 100) : 0;
                      const hasRevenue = month.total > 0;
                      return (
                        <div key={i} className="flex-1 group cursor-pointer relative h-full flex flex-col justify-end">
                          <div
                            className={`w-full rounded-t-sm transition-colors ${hasRevenue ? 'bg-navy-600 group-hover:bg-navy-500' : 'bg-slate-100'}`}
                            style={{ height: hasRevenue ? `${Math.max(heightPercent, 2)}%` : '1px' }}
                          >
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                              <div className="bg-slate-900 text-white text-[10px] px-2.5 py-2 rounded whitespace-nowrap">
                                <p className="text-slate-400">{month.date}</p>
                                <p className="font-semibold text-sm">{formatCurrency(month.total)}</p>
                                <p className="text-slate-400">{month.count} order{month.count !== 1 ? 's' : ''}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex ml-14 sm:ml-16 mt-2">
                  {yearlyMonthSales.map((month, i) => (
                    <div key={i} className="flex-1 text-center">
                      <span className="text-[9px] sm:text-xs text-slate-400">{month.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            {/* Header */}
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Total Agents</p>
                <p className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">{activeAgents + disabledAgents}</p>
                <p className="text-sm text-slate-400 mt-1">{activeAgents} active · {disabledAgents} disabled · {totalAdmins} admins</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={exportActiveAgentsCsv}
                  className="px-4 py-2 bg-white text-slate-700 border border-slate-200 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Export Active Agents CSV
                </button>
                <button
                  onClick={() => setCreateAgentModal(prev => ({ ...prev, isOpen: true, error: null, success: null }))}
                  className="px-4 py-2 bg-slate-900 text-white text-xs font-medium rounded-lg hover:bg-slate-800 transition-colors"
                >
                  + Create Agent
                </button>
              </div>
            </div>

            {/* Search */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search agents..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentAgentPage(1); }}
                className="w-full max-w-sm px-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-400 placeholder:text-slate-400 transition-colors"
              />
            </div>

            {/* Admins */}
            {adminUsers.length > 0 && (
              <div className="border border-slate-200 rounded-lg overflow-hidden mb-6">
                <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Administrators ({adminUsers.length})</h3>
                </div>
                <table className="w-full">
                  <tbody className="divide-y divide-slate-100">
                    {adminUsers.map((admin) => (
                      <tr key={admin.id}>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-3">
                            {admin.avatar_url ? (
                              <img src={admin.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className={`w-8 h-8 ${getAvatarColor(admin.first_name + admin.last_name)} rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0`}>
                                {admin.first_name?.[0]}{admin.last_name?.[0]}
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 truncate">{admin.first_name} {admin.last_name}</p>
                              <p className="text-[11px] text-slate-400 truncate">{admin.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <span className="text-[10px] font-medium text-slate-500">{admin.status || 'active'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Agents Table */}
            {(() => {
              const totalAgents = regularAgents.length;
              const totalAgentPages = Math.ceil(totalAgents / agentsPerPage);
              const agentStartIndex = (currentAgentPage - 1) * agentsPerPage;
              const paginatedAgents = regularAgents.slice(agentStartIndex, agentStartIndex + agentsPerPage);

              return (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-5 py-2.5 bg-slate-50 border-b border-slate-200">
                    <span className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">{totalAgents} agents</span>
                  </div>
                  {paginatedAgents.length === 0 ? (
                    <div className="px-5 py-16 text-center">
                      <p className="text-sm text-slate-400">{regularAgents.length === 0 ? 'No agents yet.' : 'No agents match your search.'}</p>
                    </div>
                  ) : (
                    <>
                      <table className="w-full">
                        <thead className="hidden sm:table-header-group">
                          <tr className="border-b border-slate-100">
                            <th className="text-left px-5 py-2.5 text-[11px] font-medium text-slate-400 uppercase tracking-wider">Agent</th>
                            <th className="text-left px-5 py-2.5 text-[11px] font-medium text-slate-400 uppercase tracking-wider">Contact</th>
                            <th className="text-left px-5 py-2.5 text-[11px] font-medium text-slate-400 uppercase tracking-wider">Joined</th>
                            <th className="text-left px-5 py-2.5 text-[11px] font-medium text-slate-400 uppercase tracking-wider">Status</th>
                            <th className="text-right px-5 py-2.5 text-[11px] font-medium text-slate-400 uppercase tracking-wider">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {paginatedAgents.map((agent) => (
                            <tr key={agent.id} className="hover:bg-slate-50/50 transition-colors">
                              <td className="px-5 py-3">
                                <div className="flex items-center gap-3">
                                  {agent.avatar_url ? (
                                    <img src={agent.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                                  ) : (
                                    <div className={`w-8 h-8 ${getAvatarColor(agent.first_name + agent.last_name)} rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0`}>
                                      {agent.first_name?.[0]}{agent.last_name?.[0]}
                                    </div>
                                  )}
                                  <span className="text-sm font-medium text-slate-900">{agent.first_name} {agent.last_name}</span>
                                  {agent.created_by === 'admin' && <span className="text-[10px] font-medium text-slate-400">MANUAL</span>}
                                </div>
                              </td>
                              <td className="px-5 py-3 hidden sm:table-cell">
                                <p className="text-sm text-slate-700">{agent.email}</p>
                                <p className="text-[11px] text-slate-400">{agent.phone || '-'}</p>
                              </td>
                              <td className="px-5 py-3 text-sm text-slate-500 hidden sm:table-cell">{formatDate(agent.created_at)}</td>
                              <td className="px-5 py-3 hidden sm:table-cell">
                                <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${
                                  agent.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'
                                }`}>
                                  {agent.status === 'active' ? 'Active' : 'Disabled'}
                                </span>
                              </td>
                              <td className="px-5 py-3 text-right">
                                <button
                                  onClick={() => toggleAgentStatus(agent)}
                                  className={`text-[11px] font-medium px-2.5 py-1 rounded border transition-colors ${
                                    agent.status === 'active' ? 'border-slate-200 text-red-500 hover:bg-red-50' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
                                  }`}
                                >
                                  {agent.status === 'active' ? 'Disable' : 'Enable'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      {totalAgents > 0 && (
                        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>Rows:</span>
                            <select value={agentsPerPage} onChange={(e) => { setAgentsPerPage(Number(e.target.value)); setCurrentAgentPage(1); }} className="px-2 py-1 text-xs border border-slate-200 rounded bg-white focus:outline-none">
                              {[10, 25, 50, 100].map(num => <option key={num} value={num}>{num}</option>)}
                            </select>
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-500">
                            <span>{agentStartIndex + 1}–{Math.min(agentStartIndex + agentsPerPage, totalAgents)} of {totalAgents}</span>
                            <button onClick={() => setCurrentAgentPage(prev => Math.max(1, prev - 1))} disabled={currentAgentPage === 1} className="px-2 py-1 border border-slate-200 rounded hover:bg-white disabled:opacity-30 transition-colors">Prev</button>
                            <button onClick={() => setCurrentAgentPage(prev => Math.min(totalAgentPages, prev + 1))} disabled={currentAgentPage === totalAgentPages} className="px-2 py-1 border border-slate-200 rounded hover:bg-white disabled:opacity-30 transition-colors">Next</button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}

            {/* Create Agent Modal */}
            {createAgentModal.isOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !createAgentModal.isSubmitting && resetCreateAgentModal()}>
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
                  <div className="flex items-center justify-between p-5 border-b border-slate-200">
                    <div>
                      <h3 className="text-lg font-bold text-slate-900">Create Agent Account</h3>
                      <p className="text-xs text-slate-500 mt-0.5">Credentials will be emailed to the agent</p>
                    </div>
                    <button onClick={() => !createAgentModal.isSubmitting && resetCreateAgentModal()} className="p-1 hover:bg-slate-100 rounded-lg transition-colors">
                      <X className="w-5 h-5 text-slate-400" />
                    </button>
                  </div>

                  {createAgentModal.success ? (
                    <div className="p-5 space-y-4">
                      <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
                        <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-emerald-800">{createAgentModal.success}</p>
                      </div>
                      <button onClick={resetCreateAgentModal} className="w-full py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium text-sm">
                        Done
                      </button>
                    </div>
                  ) : (
                    <div className="p-5 space-y-4">
                      {createAgentModal.error && (
                        <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-xl">
                          <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-red-700">{createAgentModal.error}</p>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">First Name *</label>
                          <input
                            type="text"
                            value={createAgentModal.firstName}
                            onChange={e => setCreateAgentModal(prev => ({ ...prev, firstName: e.target.value, error: null }))}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                            placeholder="John"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">Last Name *</label>
                          <input
                            type="text"
                            value={createAgentModal.lastName}
                            onChange={e => setCreateAgentModal(prev => ({ ...prev, lastName: e.target.value, error: null }))}
                            className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                            placeholder="Doe"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Email *</label>
                        <input
                          type="email"
                          value={createAgentModal.email}
                          onChange={e => setCreateAgentModal(prev => ({ ...prev, email: e.target.value, error: null }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                          placeholder="agent@example.com"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Phone</label>
                        <input
                          type="tel"
                          value={createAgentModal.phone}
                          onChange={e => {
                            const cleaned = e.target.value.replace(/\D/g, '');
                            const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
                            let formatted = '';
                            if (match) {
                              const [, area, exchange, num] = match;
                              if (num) formatted = `(${area}) ${exchange}-${num}`;
                              else if (exchange) formatted = `(${area}) ${exchange}`;
                              else if (area) formatted = `(${area}`;
                              else formatted = '';
                            }
                            setCreateAgentModal(prev => ({ ...prev, phone: formatted }));
                          }}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                          placeholder="(555) 123-4567"
                          maxLength={14}
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Password *</label>
                        <div className="relative">
                          <input
                            type={createAgentModal.showPassword ? 'text' : 'password'}
                            value={createAgentModal.password}
                            onChange={e => setCreateAgentModal(prev => ({ ...prev, password: e.target.value, error: null }))}
                            className="w-full px-3 py-2 pr-10 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                            placeholder="Min 6 characters"
                          />
                          <button
                            type="button"
                            onClick={() => setCreateAgentModal(prev => ({ ...prev, showPassword: !prev.showPassword }))}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                          >
                            {createAgentModal.showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Confirm Password *</label>
                        <input
                          type={createAgentModal.showPassword ? 'text' : 'password'}
                          value={createAgentModal.confirmPassword}
                          onChange={e => setCreateAgentModal(prev => ({ ...prev, confirmPassword: e.target.value, error: null }))}
                          className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
                          placeholder="Re-enter password"
                        />
                      </div>

                      <button
                        onClick={handleCreateAgent}
                        disabled={createAgentModal.isSubmitting}
                        className="w-full py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        {createAgentModal.isSubmitting ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Creating Account...
                          </>
                        ) : (
                          <>
                            <UserCheck className="w-4 h-4" />
                            Create Account & Send Credentials
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div>
            {/* Header */}
            {(() => {
              const leadOrders = orders.filter(o =>
                o.product_category !== 'Dialer Technology' && !o.product_name?.toLowerCase().includes('dialer')
              );
              return (
                <>
                <div className="flex items-end justify-between mb-6">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Lead Orders</p>
                    <p className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">{leadOrders.length}</p>
                    <p className="text-sm text-slate-400 mt-1">{leadOrders.filter(o => !o.fulfilled).length} unfulfilled · {leadOrders.filter(o => o.fulfilled).length} fulfilled</p>
                  </div>
                  <button
                    onClick={openManualOrderModal}
                    className="px-4 py-2 bg-slate-900 text-white text-xs font-medium rounded-lg hover:bg-slate-800 transition-colors"
                  >
                    + Create Order
                  </button>
                </div>

                <div className="flex items-center gap-2 mb-4">
                  {[
                    { id: 'all', label: 'All Orders', count: leadOrders.length },
                    { id: 'fulfilled', label: 'Fulfilled', count: leadOrders.filter(o => o.fulfilled).length }
                  ].map(filter => (
                <button
                  key={filter.id}
                  onClick={() => { setOrderFilter(filter.id as any); setCurrentOrderPage(1); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                    orderFilter === filter.id ? 'bg-slate-900 text-white border-slate-900' : 'text-slate-500 border-slate-200 hover:border-slate-300'
                  }`}
                >
                  {filter.label} {filter.count}
                </button>
              ))}
                </div>
                </>
              );
            })()}

            {/* Orders Table */}
            {(() => {
              const isDialerOrder = (o: Order) =>
                o.product_category === 'Dialer Technology' || o.product_name?.toLowerCase().includes('dialer');
              const filteredOrders = orders
                .filter(order => !isDialerOrder(order))
                .filter(order => { if (orderFilter === 'fulfilled') return order.fulfilled; return true; })
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
              const totalFilteredOrders = filteredOrders.length;
              const totalPages = Math.ceil(totalFilteredOrders / ordersPerPage);
              const startIndex = (currentOrderPage - 1) * ordersPerPage;
              const paginatedOrders = filteredOrders.slice(startIndex, startIndex + ordersPerPage);

              return (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  {paginatedOrders.length === 0 ? (
                    <div className="px-5 py-16 text-center">
                      <p className="text-sm text-slate-400">{orders.length === 0 ? 'No orders yet.' : 'No orders match your filter.'}</p>
                    </div>
                  ) : (
                    <div className="overflow-auto">
                      <table className="w-full min-w-[800px]">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="text-left px-5 py-2.5 text-[11px] font-medium text-slate-400 uppercase tracking-wider">Product</th>
                            <th className="text-left px-3 py-2.5 text-[11px] font-medium text-slate-400 uppercase tracking-wider">Customer</th>
                            <th className="text-left px-3 py-2.5 text-[11px] font-medium text-slate-400 uppercase tracking-wider">Details</th>
                            <th className="text-left px-3 py-2.5 text-[11px] font-medium text-slate-400 uppercase tracking-wider">Status</th>
                            <th className="text-right px-3 py-2.5 text-[11px] font-medium text-slate-400 uppercase tracking-wider">Amount</th>
                            <th className="text-right px-5 py-2.5 text-[11px] font-medium text-slate-400 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {paginatedOrders.map((order) => {
                            const agent = agents.find(a => a.user_id === order.agent_id);
                            const statesRaw = order.states;
                            const statesArray = Array.isArray(statesRaw) ? statesRaw : (typeof statesRaw === 'string' && statesRaw.trim() ? statesRaw.split(',').map(s => s.trim()).filter(Boolean) : []);
                            const hasStates = statesArray.length > 0;
                            const isStatesOpen = viewStatesOrderId === order.id;
                            return (
                              <React.Fragment key={order.id}>
                              <tr className="hover:bg-slate-50/50 transition-colors">
                                <td className="px-5 py-3">
                                  <p className="text-sm font-medium text-slate-900">{order.product_name || 'Unknown'}</p>
                                  <p className="text-[11px] text-slate-400">{formatDate(order.created_at)}{!order.shopify_order_id ? ' · Manual' : ''}</p>
                                </td>
                                <td className="px-3 py-3">
                                  <p className="text-sm text-slate-700 truncate max-w-[140px]">{agent ? `${agent.first_name} ${agent.last_name}` : (order.customer_name || 'Unknown')}</p>
                                  <p className="text-[11px] text-slate-400 truncate max-w-[140px]">{order.customer_email || agent?.email || '-'}</p>
                                </td>
                                <td className="px-3 py-3">
                                  <p className="text-xs text-slate-600">
                                    {order.quantity} {order.product_category === 'Dialer Technology' || order.product_name?.toLowerCase().includes('dialer')
                                      ? (order.quantity === 1 ? 'seat' : 'seats')
                                      : (order.quantity === 1 ? 'lead' : 'leads')}
                                  </p>
                                </td>
                                <td className="px-3 py-3">
                                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${
                                    order.fulfilled ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                    : order.notes?.includes('[FULFILLMENT_PENDING') ? 'bg-blue-50 text-blue-700 border-blue-200'
                                    : 'bg-amber-50 text-amber-700 border-amber-200'
                                  }`}>
                                    {order.fulfilled ? 'Fulfilled' : order.notes?.includes('[FULFILLMENT_PENDING') ? 'Processing' : 'Pending'}
                                  </span>
                                </td>
                                <td className="px-3 py-3 text-right">
                                  <p className="text-sm font-semibold text-slate-900">{formatCurrency(order.price || 0)}</p>
                                </td>
                                <td className="px-5 py-3 text-right">
                                  <div className="flex items-center justify-end gap-1.5">
                                    {hasStates && (
                                      <button
                                        onClick={() => setViewStatesOrderId(isStatesOpen ? null : order.id)}
                                        className={`text-[11px] font-medium px-2.5 py-1 border rounded transition-colors ${
                                          isStatesOpen ? 'bg-blue-50 text-blue-600 border-blue-200' : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                                        }`}
                                      >
                                        {isStatesOpen ? 'Hide States' : `States (${statesArray.length})`}
                                      </button>
                                    )}
                                    {order.fulfilled ? (
                                      <>
                                        <button onClick={() => openFulfillmentModal(order, true)} className="text-[11px] font-medium px-2.5 py-1 border border-slate-200 text-slate-500 rounded hover:bg-slate-50 transition-colors">Sheet</button>
                                        <button onClick={() => unfulfillOrder(order)} className="text-[11px] font-medium px-2.5 py-1 border border-slate-200 text-slate-500 rounded hover:bg-red-50 hover:text-red-500 transition-colors">Undo</button>
                                      </>
                                    ) : order.notes?.includes('[FULFILLMENT_PENDING') ? (
                                      <span className="text-[11px] font-medium px-3 py-1 bg-blue-100 text-blue-600 rounded cursor-default">Awaiting Confirmation</span>
                                    ) : (
                                      <button onClick={() => openFulfillmentModal(order)} className="text-[11px] font-medium px-3 py-1 bg-slate-900 text-white rounded hover:bg-slate-800 transition-colors">Fulfill</button>
                                    )}
                                    <button onClick={() => openEditOrderModal(order)} className="text-[11px] font-medium px-2.5 py-1 border border-slate-200 text-slate-500 rounded hover:bg-slate-50 transition-colors">Edit</button>
                                    <button onClick={() => deleteManualOrder(order)} className="text-[11px] font-medium px-2.5 py-1 border border-slate-200 text-red-500 rounded hover:bg-red-50 transition-colors">Delete</button>
                                  </div>
                                </td>
                              </tr>
                              {isStatesOpen && hasStates && (
                                <tr className="bg-blue-50/40">
                                  <td colSpan={6} className="px-5 py-2.5">
                                    <div className="flex flex-wrap items-center gap-1.5">
                                      <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mr-1">Selected States:</span>
                                      {statesArray.map((state: string) => (
                                        <span key={state} className="text-[11px] font-medium px-2 py-0.5 bg-white text-slate-700 rounded border border-slate-200">
                                          {state}
                                        </span>
                                      ))}
                                    </div>
                                  </td>
                                </tr>
                              )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Pagination */}
                  {totalFilteredOrders > 0 && (
                    <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>Rows:</span>
                        <select value={ordersPerPage} onChange={(e) => { setOrdersPerPage(Number(e.target.value)); setCurrentOrderPage(1); }} className="px-2 py-1 text-xs border border-slate-200 rounded bg-white focus:outline-none">
                          {[10, 25, 50, 100].map(num => <option key={num} value={num}>{num}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>{startIndex + 1}–{Math.min(startIndex + ordersPerPage, totalFilteredOrders)} of {totalFilteredOrders}</span>
                        <button onClick={() => setCurrentOrderPage(prev => Math.max(1, prev - 1))} disabled={currentOrderPage === 1} className="px-2 py-1 border border-slate-200 rounded hover:bg-white disabled:opacity-30 transition-colors">Prev</button>
                        <button onClick={() => setCurrentOrderPage(prev => Math.min(totalPages, prev + 1))} disabled={currentOrderPage === totalPages} className="px-2 py-1 border border-slate-200 rounded hover:bg-white disabled:opacity-30 transition-colors">Next</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div className="space-y-6">
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                <h3 className="font-medium text-slate-900">Product Performance</h3>
              </div>
              
              {productBreakdown.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Package className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p>No sales data yet</p>
                </div>
              ) : (
                <div className="p-3 sm:p-4 space-y-3">
                  {productBreakdown.map((product, i) => {
                    const maxRevenue = productBreakdown[0]?.revenue || 1;
                    const percentage = (product.revenue / maxRevenue) * 100;
                    
                    return (
                      <div key={i} className="p-3 sm:p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-start sm:items-center justify-between gap-2 mb-2">
                          <span className="font-medium text-slate-900 text-sm sm:text-base min-w-0 truncate">{product.name}</span>
                          <span className="font-bold text-slate-900 text-sm sm:text-base flex-shrink-0">{formatCurrency(product.revenue)}</span>
                        </div>
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                            <div className="h-full bg-navy-500 rounded-full" style={{ width: `${percentage}%` }} />
                          </div>
                          <span className="text-xs sm:text-sm text-slate-500 flex-shrink-0">{product.count} orders</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

          </div>
        )}

        {/* API Keys Tab */}
        {/* ==================== DIALER SUBS TAB ==================== */}
        {activeTab === 'dialer-subs' && (
          <div>
            {/* Header */}
            <div className="flex items-end justify-between mb-6">
              <div>
                <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Monthly Revenue</p>
                <p className="text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">${dialerStats.monthlyRevenue.toLocaleString()}</p>
                <p className="text-sm text-slate-400 mt-1">{dialerSubs.length} total subscriptions</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const headers = ['Agent', 'Email', 'Product', 'Status', 'Started', 'Expires', 'Price', 'Free'];
                    const rows = dialerSubs.map(s => [
                      s.agent_name ?? '', s.agent_email ?? '', s.product_name, s.status,
                      s.started_at ? new Date(s.started_at).toLocaleDateString() : '',
                      s.duration_days === -1 ? 'Unlimited' : s.expires_at ? new Date(s.expires_at).toLocaleDateString() : '',
                      String(s.product_price ?? 0), s.is_free ? 'Yes' : 'No',
                    ]);
                    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `dialer-subs-${new Date().toISOString().split('T')[0]}.csv`; a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="px-3 py-2 border border-slate-200 text-slate-500 text-xs font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Export CSV
                </button>
                <button
                  onClick={() => setDialerCreateModal({ isOpen: true, agentId: '', productId: DIALER_PRODUCT_IDS[0], productIds: [], duration: DEFAULT_DURATIONS[DIALER_PRODUCT_IDS[0]], notes: '', username: '', password: '', isFree: false, customStartDate: '', customExpiryDate: '' })}
                  className="px-4 py-2 bg-slate-900 text-white text-xs font-medium rounded-lg hover:bg-slate-800 transition-colors"
                >
                  + New Subscription
                </button>
              </div>
            </div>

            {dialerLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-5 h-5 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-4" />
                <p className="text-sm text-slate-400">Loading subscriptions...</p>
              </div>
            ) : (
              <>
                {/* ─── NOVEMBER MISSED INBOUND DIALER, PURCHASER LIST ─── */}
                {(() => {
                  const enrollmentSubs = [...dialerSubs.filter(isMissedInboundDialerSub)]
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                  if (enrollmentSubs.length === 0) return null;

                  return (
                    <div className="mb-6 rounded-xl border border-red-900 bg-white overflow-hidden">
                      <div className="px-5 py-4 border-b border-red-950 bg-gradient-to-r from-red-700 via-red-950 to-black flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="w-2 h-2 rounded-full bg-red-300" />
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-red-100">Featured Product</p>
                            <h3 className="text-sm font-semibold text-white mt-0.5">November Missed Inbound Dialer: Purchasers</h3>
                          </div>
                        </div>
                        <span className="text-xs font-semibold text-white">{enrollmentSubs.length} {enrollmentSubs.length === 1 ? 'purchaser' : 'purchasers'}</span>
                      </div>

                      <div className="max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                            <tr className="text-[10px] text-slate-500 uppercase tracking-wider">
                              <th className="text-left px-5 py-2.5 font-semibold">Agent</th>
                              <th className="text-left px-5 py-2.5 font-semibold hidden sm:table-cell">Email</th>
                              <th className="text-left px-5 py-2.5 font-semibold">Purchased</th>
                              <th className="text-left px-5 py-2.5 font-semibold hidden md:table-cell">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {enrollmentSubs.map(s => {
                              const statusStyles: Record<DialerSubscriptionStatus, string> = {
                                active: 'bg-emerald-50 text-emerald-700',
                                expiring_soon: 'bg-amber-50 text-amber-700',
                                pending: 'bg-slate-100 text-slate-600',
                                expired: 'bg-slate-50 text-slate-400',
                              };
                              const statusLabel: Record<DialerSubscriptionStatus, string> = {
                                active: 'On dialer',
                                expiring_soon: 'Expiring soon',
                                pending: 'Pending setup',
                                expired: 'Off dialer',
                              };
                              return (
                                <tr key={s.id} className="hover:bg-slate-50/60 transition-colors">
                                  <td className="px-5 py-3 text-slate-900 font-medium whitespace-nowrap">{s.agent_name || 'Unknown'}</td>
                                  <td className="px-5 py-3 text-slate-500 hidden sm:table-cell truncate max-w-[240px]">{s.agent_email || '-'}</td>
                                  <td className="px-5 py-3 text-slate-600 whitespace-nowrap">
                                    {s.created_at
                                      ? new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                      : '-'}
                                  </td>
                                  <td className="px-5 py-3 hidden md:table-cell whitespace-nowrap">
                                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${statusStyles[s.status]}`}>
                                      {statusLabel[s.status]}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* ─── OCTOBER MISSED INBOUND DIALER, PRIOR-CYCLE PURCHASER LIST ─── */}
                {/* Separate bucket from November. Expired October subs are hidden, so
                    the panel drains as enrollments run out and disappears
                    entirely once the last October sub expires. */}
                {(() => {
                  const octoberSubs = dialerSubs
                    .filter(isOctoberMissedInboundDialerSub)
                    .filter(s => s.status !== 'expired')
                    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
                  if (octoberSubs.length === 0) return null;

                  return (
                    <div className="mb-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
                      <div className="px-5 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 via-slate-50 to-slate-50 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className="w-2 h-2 rounded-full bg-slate-400" />
                          <div>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500/80">Prior Cycle</p>
                            <h3 className="text-sm font-semibold text-slate-900 mt-0.5">October Missed Inbound Dialer: Purchasers</h3>
                          </div>
                        </div>
                        <span className="text-xs font-semibold text-slate-700">{octoberSubs.length} active {octoberSubs.length === 1 ? 'purchaser' : 'purchasers'}</span>
                      </div>

                      <div className="max-h-96 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                            <tr className="text-[10px] text-slate-500 uppercase tracking-wider">
                              <th className="text-left px-5 py-2.5 font-semibold">Agent</th>
                              <th className="text-left px-5 py-2.5 font-semibold hidden sm:table-cell">Email</th>
                              <th className="text-left px-5 py-2.5 font-semibold">Purchased</th>
                              <th className="text-left px-5 py-2.5 font-semibold hidden md:table-cell">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {octoberSubs.map(s => {
                              const statusStyles: Record<DialerSubscriptionStatus, string> = {
                                active: 'bg-emerald-50 text-emerald-700',
                                expiring_soon: 'bg-amber-50 text-amber-700',
                                pending: 'bg-slate-100 text-slate-600',
                                expired: 'bg-slate-50 text-slate-400',
                              };
                              const statusLabel: Record<DialerSubscriptionStatus, string> = {
                                active: 'On dialer',
                                expiring_soon: 'Expiring soon',
                                pending: 'Pending setup',
                                expired: 'Off dialer',
                              };
                              return (
                                <tr key={s.id} className="hover:bg-slate-50/60 transition-colors">
                                  <td className="px-5 py-3 text-slate-900 font-medium whitespace-nowrap">{s.agent_name || 'Unknown'}</td>
                                  <td className="px-5 py-3 text-slate-500 hidden sm:table-cell truncate max-w-[240px]">{s.agent_email || '-'}</td>
                                  <td className="px-5 py-3 text-slate-600 whitespace-nowrap">
                                    {s.created_at
                                      ? new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                      : '-'}
                                  </td>
                                  <td className="px-5 py-3 hidden md:table-cell whitespace-nowrap">
                                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${statusStyles[s.status]}`}>
                                      {statusLabel[s.status]}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()}

                {/* Stats Row */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-px bg-slate-200 rounded-lg overflow-hidden mb-6">
                  {[
                    { label: 'Active', value: dialerStats.active },
                    { label: 'Pending', value: dialerStats.pending },
                    { label: 'Expiring Soon', value: dialerStats.expiringSoon },
                    { label: 'Expired', value: dialerStats.expired },
                    { label: 'Free', value: freeDialerCount },
                  ].map((s) => (
                    <div key={s.label} className="bg-white p-4">
                      <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">{s.label}</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* Product Distribution: recompute locally so the November Missed
                    Inbound bucket only includes authenticated real purchasers
                    (rather than every sub that happens to live on slot 25). */}
                {(() => {
                  const activeSubs = dialerSubs.filter(s => s.status === 'active' || s.status === 'expiring_soon' || s.status === 'pending');
                  const counts = new Map<number, number>();
                  activeSubs.forEach(s => {
                    const pid = isMissedInboundDialerSub(s) ? 25 : resolveDialerProductId(s.product_id, s.product_name);
                    if (pid === 25 && !isMissedInboundDialerSub(s)) return;
                    counts.set(pid, (counts.get(pid) ?? 0) + 1);
                  });
                  const products = Array.from(counts.entries()).map(([pid, count]) => ({
                    product: DIALER_PRODUCT_NAMES[pid] ?? `Product #${pid}`,
                    count,
                  }));
                  if (products.length === 0) return null;
                  const totalActive = products.reduce((s, p) => s + p.count, 0);
                  return (
                    <div className="border border-slate-200 rounded-lg overflow-hidden mb-6">
                      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Subscriptions by Product</h3>
                        <span className="text-sm font-semibold text-slate-900">{totalActive} active/pending</span>
                      </div>
                      <table className="w-full">
                        <tbody className="divide-y divide-slate-100">
                          {[...products].sort((a, b) => b.count - a.count).map((item, i) => {
                            const pct = totalActive > 0 ? Math.round((item.count / totalActive) * 100) : 0;
                            return (
                              <tr key={i}>
                                <td className="px-5 py-3 text-sm text-slate-700 font-medium">{item.product}</td>
                                <td className="px-5 py-3 text-sm font-semibold text-slate-900 text-right">{item.count}</td>
                                <td className="px-5 py-3 text-right w-24">
                                  <div className="flex items-center gap-2 justify-end">
                                    <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                      <div className="h-full bg-navy-600 rounded-full" style={{ width: `${pct}%` }} />
                                    </div>
                                    <span className="text-xs text-slate-400 w-8 text-right">{pct}%</span>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}

                {/* Agents by Product */}
                {(() => {
                  const activeSubs = dialerSubs.filter(s => s.status === 'active' || s.status === 'expiring_soon' || s.status === 'pending');
                  const grouped = new Map<number, DialerSubWithAgent[]>();
                  activeSubs.forEach(s => {
                    const pid = isMissedInboundDialerSub(s) ? 25 : resolveDialerProductId(s.product_id, s.product_name);
                    // For the November Missed Inbound Dialer (id 25), only include
                    // subs authenticated as real enrollment purchasers. Anything
                    // else on slot 25 (legacy admin edits, test data, etc.)
                    // is excluded so it can't be mislabeled as a real purchase.
                    if (pid === 25 && !isMissedInboundDialerSub(s)) return;
                    if (!grouped.has(pid)) grouped.set(pid, []);
                    grouped.get(pid)!.push(s);
                  });
                  const productOrder = DIALER_PRODUCT_IDS.filter(id => grouped.has(id));
                  const toggleProduct = (pid: number) => {
                    setExpandedDialerProducts(prev => {
                      const next = new Set(prev);
                      next.has(pid) ? next.delete(pid) : next.add(pid);
                      return next;
                    });
                  };

                  if (productOrder.length === 0) return null;

                  return (
                    <div className="border border-slate-200 rounded-lg overflow-hidden mb-6">
                      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Agents by Product</h3>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {productOrder.map(pid => {
                          const agents = grouped.get(pid) ?? [];
                          const isExpanded = expandedDialerProducts.has(pid);
                          const uniqueAgents = Array.from(
                            new Map(agents.map(a => [a.agent_id, a])).values()
                          ).sort((a, b) => (a.agent_name ?? '').localeCompare(b.agent_name ?? ''));

                          return (
                            <div key={pid}>
                              <button
                                onClick={() => toggleProduct(pid)}
                                className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors text-left"
                              >
                                <span className="text-sm font-medium text-slate-700">{DIALER_PRODUCT_NAMES[pid] ?? `Product #${pid}`}</span>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-semibold text-slate-900">{uniqueAgents.length}</span>
                                  <span className="text-xs text-slate-400">{isExpanded ? '▲' : '▼'}</span>
                                </div>
                              </button>
                              {isExpanded && (
                                <div className="border-t border-slate-100">
                                  <table className="w-full">
                                    <tbody className="divide-y divide-slate-50">
                                      {uniqueAgents.map(agent => (
                                        <tr key={agent.agent_id}>
                                          <td className="pl-10 pr-5 py-2.5 text-sm text-slate-700">{agent.agent_name ?? 'Unknown'}</td>
                                          <td className="px-5 py-2.5 text-xs text-slate-400">{agent.agent_email ?? ''}</td>
                                          <td className="px-5 py-2.5 text-right">
                                            <span className="text-[10px] font-medium text-slate-500">
                                              {agent.status === 'pending' ? 'Pending' : agent.status === 'expiring_soon' ? 'Expiring' : 'Active'}
                                            </span>
                                            {agent.is_free && <span className="ml-2 text-[10px] font-semibold text-slate-400">FREE</span>}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Filters */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                  <input
                    type="text"
                    placeholder="Search by agent or product..."
                    value={dialerSearch}
                    onChange={(e) => { setDialerSearch(e.target.value); setCurrentDialerPage(1); }}
                    className="flex-1 max-w-sm px-4 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy-500/20 focus:border-navy-400 placeholder:text-slate-400 transition-colors"
                  />
                  <div className="flex gap-1 overflow-x-auto">
                    {([
                      { label: 'All', value: 'all' as const, count: dialerSubs.length },
                      { label: 'Pending', value: 'pending' as const, count: dialerStatusCounts['pending'] ?? 0 },
                      { label: 'Active', value: 'active' as const, count: dialerStatusCounts['active'] ?? 0 },
                      { label: 'Expiring', value: 'expiring_soon' as const, count: dialerStatusCounts['expiring_soon'] ?? 0 },
                      { label: 'Expired', value: 'expired' as const, count: dialerStatusCounts['expired'] ?? 0 },
                      { label: 'Free', value: 'free' as const, count: freeDialerCount },
                    ]).map(t => (
                      <button
                        key={t.value}
                        onClick={() => { setDialerFilter(t.value); setCurrentDialerPage(1); }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap border ${
                          dialerFilter === t.value ? 'bg-slate-900 text-white border-slate-900' : 'text-slate-500 border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        {t.label} {t.count > 0 ? t.count : ''}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Subscription Table */}
                {(() => {
                  const totalFiltered = filteredDialerSubs.length;
                  const totalDialerPages = Math.max(1, Math.ceil(totalFiltered / dialerSubsPerPage));
                  const dialerStartIndex = (currentDialerPage - 1) * dialerSubsPerPage;
                  const paginatedSubs = filteredDialerSubs.slice(dialerStartIndex, dialerStartIndex + dialerSubsPerPage);
                  const allPageIds = paginatedSubs.map(s => s.id);
                  const allPageSelected = allPageIds.length > 0 && allPageIds.every(id => selectedDialerSubs.has(id));
                  return (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  {/* Table Header */}
                  <div className="bg-slate-50 border-b border-slate-200 px-5 py-2.5 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => toggleAllDialerSubs(allPageIds)}
                      className={`w-4 h-4 rounded border transition-colors flex-shrink-0 flex items-center justify-center ${
                        allPageSelected ? 'bg-slate-800 border-slate-800' : 'border-slate-300 bg-white'
                      }`}
                    >
                      {allPageSelected && <span className="text-white text-[9px]">✓</span>}
                    </button>
                    <span className="text-[11px] text-slate-400 uppercase tracking-wider font-medium flex-1">
                      {selectedDialerSubs.size > 0 ? `${selectedDialerSubs.size} selected` : `${totalFiltered} subscriptions`}
                    </span>
                  </div>

                  {/* Subscription Rows */}
                  <div className="divide-y divide-slate-100">
                    {paginatedSubs.map(sub => {
                      const daysLeft = getDaysRemaining(sub.expires_at);
                      const daysSincePaid = getDaysSincePaid(sub.created_at);
                      const isChecked = selectedDialerSubs.has(sub.id);
                      return (
                        <div key={sub.id} className={`${isChecked ? 'bg-slate-50' : 'bg-white'} hover:bg-slate-50/50 transition-colors`}>
                          {/* Main Row */}
                          <div className="px-5 py-3 flex items-center gap-3">
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); toggleDialerSubSelection(sub.id); }}
                              className={`w-4 h-4 rounded border transition-colors flex-shrink-0 flex items-center justify-center ${
                                isChecked ? 'bg-slate-800 border-slate-800' : 'border-slate-300 bg-white'
                              }`}
                            >
                              {isChecked && <span className="text-white text-[9px]">✓</span>}
                            </button>
                            <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto] gap-2 sm:gap-4 items-center">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-900 truncate">{sub.agent_name ?? 'Unknown'}</p>
                                <p className="text-[11px] text-slate-400 truncate">{sub.agent_email ?? ''}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500">{sub.product_name}</span>
                                {sub.is_free && <span className="text-[10px] font-semibold text-slate-400">FREE</span>}
                                {!sub.is_free && <span className="text-[10px] text-slate-400">${(sub.product_price ?? 0).toLocaleString()}</span>}
                              </div>
                              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${dialerStatusBadge[sub.status]}`}>
                                {dialerStatusLabel[sub.status]}
                              </span>
                              <span className={`text-xs font-semibold tabular-nums ${
                                sub.duration_days === -1 ? 'text-slate-500' :
                                sub.status === 'expired' ? 'text-red-500' :
                                sub.status === 'pending' ? (daysSincePaid >= 2 ? 'text-red-500' : 'text-slate-500') :
                                dialerDaysColor(daysLeft)
                              }`}>
                                {sub.duration_days === -1 ? '∞' :
                                 sub.status === 'expired' ? 'Expired' :
                                 sub.status === 'pending' ? `${daysSincePaid}d waiting` :
                                 `${daysLeft}d left`}
                              </span>
                            </div>
                          </div>

                          {/* Details Row */}
                          <div className="px-5 pb-3 pl-12 flex flex-wrap items-center gap-x-5 gap-y-1 text-[11px] text-slate-400">
                            <span>Paid {formatDialerDate(sub.created_at)}</span>
                            <span>Started {sub.started_at ? formatDialerDate(sub.started_at) : '-'}</span>
                            <span>Expires {sub.duration_days === -1 ? 'Never' : sub.expires_at ? formatDialerDateTime(sub.expires_at) : '-'}</span>
                            {sub.dialer_username && (sub.status === 'active' || sub.status === 'expiring_soon') && (
                              <span className="font-mono">{sub.dialer_username} / ••••••••</span>
                            )}
                          </div>

                          {/* Actions Row */}
                          <div className="px-5 pb-3 pl-12 flex items-center gap-1.5 flex-wrap">
                            {sub.status === 'pending' && (
                              <button
                                onClick={() => setDialerStartModal({ isOpen: true, subId: sub.id, username: '', password: '', duration: sub.duration_days })}
                                disabled={dialerActionLoading === sub.id}
                                className="text-[11px] font-medium px-3 py-1 bg-slate-900 text-white rounded hover:bg-slate-800 transition-colors disabled:opacity-50"
                              >
                                {dialerActionLoading === sub.id ? 'Loading...' : 'Start Campaign'}
                              </button>
                            )}
                            {(sub.status === 'active' || sub.status === 'expiring_soon') && (
                              <>
                                <button onClick={() => setDialerEditCredsModal({ isOpen: true, subId: sub.id, username: sub.dialer_username ?? '', password: sub.dialer_password ?? '' })} disabled={dialerActionLoading === sub.id} className="text-[11px] font-medium px-2.5 py-1 border border-slate-200 text-slate-500 rounded hover:bg-slate-50 transition-colors disabled:opacity-50">Edit Creds</button>
                                <button onClick={() => setDialerRenewModal({ isOpen: true, subId: sub.id, additionalDays: DEFAULT_DURATIONS[sub.product_id] ?? 30, currentExpiry: sub.expires_at, productId: sub.product_id })} disabled={dialerActionLoading === sub.id} className="text-[11px] font-medium px-2.5 py-1 border border-slate-200 text-slate-500 rounded hover:bg-slate-50 transition-colors disabled:opacity-50">Renew</button>
                                <button onClick={() => handleDialerExpire(sub.id)} disabled={dialerActionLoading === sub.id} className="text-[11px] font-medium px-2.5 py-1 border border-slate-200 text-red-500 rounded hover:bg-red-50 transition-colors disabled:opacity-50">Expire</button>
                              </>
                            )}
                            {sub.status === 'expired' && (
                              <button onClick={() => setDialerRenewModal({ isOpen: true, subId: sub.id, additionalDays: DEFAULT_DURATIONS[sub.product_id] ?? 30, currentExpiry: sub.expires_at, productId: sub.product_id })} disabled={dialerActionLoading === sub.id} className="text-[11px] font-medium px-2.5 py-1 border border-slate-200 text-slate-500 rounded hover:bg-slate-50 transition-colors disabled:opacity-50">Renew</button>
                            )}
                            <button onClick={() => setDialerChangeProductModal({ isOpen: true, subId: sub.id, currentProductId: sub.product_id, newProductId: sub.product_id })} disabled={dialerActionLoading === sub.id} className="text-[11px] font-medium px-2.5 py-1 border border-slate-200 text-slate-500 rounded hover:bg-slate-50 transition-colors disabled:opacity-50">Switch</button>
                            <button onClick={() => setDialerEditStatesModal({ isOpen: true, subId: sub.id, orderId: sub.order_id, states: sub.states || '' })} disabled={dialerActionLoading === sub.id} className="text-[11px] font-medium px-2.5 py-1 border border-slate-200 text-slate-500 rounded hover:bg-slate-50 transition-colors disabled:opacity-50">States</button>
                            <button
                              onClick={() => {
                                const toLocal = (iso: string | null) => {
                                  if (!iso) return '';
                                  const d = new Date(iso);
                                  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
                                };
                                setDialerEditDatesModal({ isOpen: true, subId: sub.id, startedAt: toLocal(sub.started_at), expiresAt: toLocal(sub.expires_at) });
                              }}
                              disabled={dialerActionLoading === sub.id}
                              className="text-[11px] font-medium px-2.5 py-1 border border-slate-200 text-slate-500 rounded hover:bg-slate-50 transition-colors disabled:opacity-50"
                            >Edit Dates</button>
                            <button onClick={() => handleDialerDelete(sub.id)} disabled={dialerActionLoading === sub.id} className="text-[11px] font-medium px-2.5 py-1 border border-slate-200 text-red-500 rounded hover:bg-red-50 transition-colors disabled:opacity-50">Delete</button>
                          </div>

                          {/* States */}
                          {sub.states && sub.states.trim() && (
                            <div className="px-5 pb-3 pl-12">
                              <div className="flex flex-wrap items-center gap-1">
                                <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wider mr-1">States:</span>
                                {sub.states.split(',').map((s: string) => s.trim()).filter(Boolean).map((state: string) => (
                                  <span key={state} className="text-[10px] font-medium px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">
                                    {state}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    {paginatedSubs.length === 0 && (
                      <div className="px-5 py-16 text-center">
                        <p className="text-sm text-slate-400">{dialerSubs.length === 0 ? 'No subscriptions yet.' : 'No subscriptions match your filter.'}</p>
                      </div>
                    )}
                  </div>

                  {/* Pagination */}
                  {totalFiltered > 0 && (
                    <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex items-center justify-between">
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <span>Rows:</span>
                        <select
                          value={dialerSubsPerPage}
                          onChange={(e) => { setDialerSubsPerPage(Number(e.target.value)); setCurrentDialerPage(1); }}
                          className="px-2 py-1 text-xs border border-slate-200 rounded bg-white focus:outline-none"
                        >
                          {[10, 25, 50].map(num => <option key={num} value={num}>{num}</option>)}
                        </select>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>{dialerStartIndex + 1}–{Math.min(dialerStartIndex + dialerSubsPerPage, totalFiltered)} of {totalFiltered}</span>
                        <button onClick={() => setCurrentDialerPage(prev => Math.max(1, prev - 1))} disabled={currentDialerPage === 1} className="px-2 py-1 border border-slate-200 rounded hover:bg-white disabled:opacity-30 transition-colors">Prev</button>
                        <button onClick={() => setCurrentDialerPage(prev => Math.min(totalDialerPages, prev + 1))} disabled={currentDialerPage === totalDialerPages} className="px-2 py-1 border border-slate-200 rounded hover:bg-white disabled:opacity-30 transition-colors">Next</button>
                      </div>
                    </div>
                  )}
                </div>
                  );
                })()}

                {/* Floating bulk action bar */}
                {selectedDialerSubs.size > 0 && (
                  <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-lg shadow-2xl px-5 py-2.5 flex items-center gap-4 text-xs">
                    <span className="font-medium">{selectedDialerSubs.size} selected</span>
                    <div className="w-px h-4 bg-slate-700" />
                    <button onClick={handleBulkDialerExpire} disabled={dialerActionLoading === 'bulk'} className="font-medium hover:text-orange-300 transition-colors disabled:opacity-50">Expire All</button>
                    <button onClick={handleBulkDialerDelete} disabled={dialerActionLoading === 'bulk'} className="font-medium hover:text-red-300 transition-colors disabled:opacity-50">Delete All</button>
                    <button onClick={() => setSelectedDialerSubs(new Set())} className="font-medium text-slate-400 hover:text-white transition-colors">Clear</button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Start Campaign Modal */}
        {dialerStartModal.isOpen && (() => {
          const sub = dialerStartModal.subId ? dialerSubs.find(s => s.id === dialerStartModal.subId) : null;
          const daysSincePaid = sub ? getDaysSincePaid(sub.created_at) : 0;
          return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setDialerStartModal({ isOpen: false, subId: null, username: '', password: '', duration: 30 })}>
              <div className="fixed inset-0 bg-black/50" />
              <div className="relative bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-[420px] animate-[modalSlideIn_0.15s_ease-out]" onClick={e => e.stopPropagation()}>
                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-base font-semibold text-slate-900">Start Campaign</h2>
                    <button onClick={() => setDialerStartModal({ isOpen: false, subId: null, username: '', password: '', duration: 30 })} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors -mr-1">
                      <X className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">Enter credentials to activate this subscription.</p>
                </div>

                {sub && (
                  <div className="mx-6 mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-xs font-bold text-emerald-700 shrink-0">
                        {(sub.agent_name ?? 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{sub.agent_name}</p>
                        <p className="text-[11px] text-slate-400 truncate">{sub.product_name} &middot; ${(sub.product_price ?? 0).toLocaleString()}</p>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-[11px] font-medium text-slate-500">{formatDialerDate(sub.created_at)}</p>
                      {daysSincePaid > 0 && (
                        <p className={`text-[10px] font-semibold ${daysSincePaid >= 2 ? 'text-red-500' : 'text-amber-500'}`}>
                          {daysSincePaid}d ago
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="px-6 pb-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Username</label>
                      <input
                        type="text"
                        value={dialerStartModal.username}
                        onChange={e => setDialerStartModal(prev => ({ ...prev, username: e.target.value }))}
                        placeholder="Dialer username"
                        autoFocus
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 placeholder:text-slate-300 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Password</label>
                      <input
                        type="text"
                        value={dialerStartModal.password}
                        onChange={e => setDialerStartModal(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Dialer password"
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 placeholder:text-slate-300 transition-all"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Duration (days)</label>
                    <input
                      type="number"
                      min={1}
                      value={dialerStartModal.duration}
                      onChange={e => setDialerStartModal(prev => ({ ...prev, duration: Math.max(1, parseInt(e.target.value) || 1) }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all"
                    />
                  </div>
                  <div className="bg-emerald-50 rounded-lg px-3.5 py-2.5 flex items-center justify-between">
                    <span className="text-[11px] font-medium text-emerald-600">Expires</span>
                    <span className="text-xs font-semibold text-emerald-800">{new Date(Date.now() + dialerStartModal.duration * 86400000).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York', timeZoneName: 'short' })}</span>
                  </div>
                </div>

                <div className="px-6 pb-6 flex gap-3">
                  <button onClick={() => setDialerStartModal({ isOpen: false, subId: null, username: '', password: '', duration: 30 })} className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                    Cancel
                  </button>
                  <button
                    onClick={handleDialerStartCampaign}
                    disabled={!dialerStartModal.username.trim() || !dialerStartModal.password.trim() || dialerActionLoading === dialerStartModal.subId}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {dialerActionLoading === dialerStartModal.subId && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                    Start Campaign
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Edit Credentials Modal */}
        {dialerEditCredsModal.isOpen && (() => {
          const sub = dialerEditCredsModal.subId ? dialerSubs.find(s => s.id === dialerEditCredsModal.subId) : null;
          return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setDialerEditCredsModal({ isOpen: false, subId: null, username: '', password: '' })}>
              <div className="fixed inset-0 bg-black/50" />
              <div className="relative bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-[420px] animate-[modalSlideIn_0.15s_ease-out]" onClick={e => e.stopPropagation()}>
                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-base font-semibold text-slate-900">Edit Credentials</h2>
                    <button onClick={() => setDialerEditCredsModal({ isOpen: false, subId: null, username: '', password: '' })} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors -mr-1">
                      <X className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">Update dialer login. This does not reset the timer.</p>
                </div>
                {sub && (
                  <div className="mx-6 mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">
                        {(sub.agent_name ?? 'U').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{sub.agent_name}</p>
                        <p className="text-[11px] text-slate-400">{sub.product_name}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-[11px] text-slate-500 font-medium">{formatDialerDateTime(sub.expires_at)}</p>
                    </div>
                  </div>
                )}
                <div className="px-6 pb-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Username</label>
                    <input
                      type="text"
                      value={dialerEditCredsModal.username}
                      onChange={e => setDialerEditCredsModal(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="Enter username"
                      autoFocus
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-300 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Password</label>
                    <input
                      type="text"
                      value={dialerEditCredsModal.password}
                      onChange={e => setDialerEditCredsModal(prev => ({ ...prev, password: e.target.value }))}
                      placeholder="Enter password"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 placeholder:text-slate-300 transition-all"
                    />
                  </div>
                </div>
                <div className="px-6 pb-6 flex gap-3">
                  <button onClick={() => setDialerEditCredsModal({ isOpen: false, subId: null, username: '', password: '' })} className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">Cancel</button>
                  <button onClick={handleDialerUpdateCreds} disabled={!dialerEditCredsModal.username.trim() || !dialerEditCredsModal.password.trim() || dialerActionLoading === dialerEditCredsModal.subId} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-slate-800 rounded-lg hover:bg-slate-900 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {dialerActionLoading === dialerEditCredsModal.subId && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                    Save Credentials
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Create Subscription Modal */}
        {dialerCreateModal.isOpen && <CreateSubscriptionModal
          agents={agents}
          dialerCreateModal={dialerCreateModal}
          setDialerCreateModal={setDialerCreateModal}
          handleDialerCreate={handleDialerCreate}
          dialerActionLoading={dialerActionLoading}
        />}

        {/* Edit Dates Modal */}
        {dialerEditDatesModal.isOpen && (() => {
          const sub = dialerEditDatesModal.subId ? dialerSubs.find(s => s.id === dialerEditDatesModal.subId) : null;
          return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setDialerEditDatesModal({ isOpen: false, subId: null, startedAt: '', expiresAt: '' })}>
              <div className="fixed inset-0 bg-black/50" />
              <div className="relative bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-[420px] animate-[modalSlideIn_0.15s_ease-out]" onClick={e => e.stopPropagation()}>
                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-base font-semibold text-slate-900">Edit Dates</h2>
                    <button onClick={() => setDialerEditDatesModal({ isOpen: false, subId: null, startedAt: '', expiresAt: '' })} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors -mr-1">
                      <X className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">Manually set start and expiry dates.</p>
                </div>

                {sub && (
                  <div className="mx-6 mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700 shrink-0">
                      {(sub.agent_name ?? 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{sub.agent_name}</p>
                      <p className="text-[11px] text-slate-400 truncate">{sub.product_name}</p>
                    </div>
                  </div>
                )}

                <div className="px-6 pb-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Started At</label>
                    <input
                      type="datetime-local"
                      value={dialerEditDatesModal.startedAt}
                      onChange={e => setDialerEditDatesModal(prev => ({ ...prev, startedAt: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Expires At</label>
                    <input
                      type="datetime-local"
                      value={dialerEditDatesModal.expiresAt}
                      onChange={e => setDialerEditDatesModal(prev => ({ ...prev, expiresAt: e.target.value }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                    />
                  </div>
                </div>

                <div className="px-6 pb-6 flex gap-3">
                  <button onClick={() => setDialerEditDatesModal({ isOpen: false, subId: null, startedAt: '', expiresAt: '' })} className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">
                    Cancel
                  </button>
                  <button
                    onClick={handleDialerEditDates}
                    disabled={dialerActionLoading === dialerEditDatesModal.subId}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {dialerActionLoading === dialerEditDatesModal.subId && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                    Save Dates
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Renew Subscription Modal */}
        {dialerRenewModal.isOpen && (() => {
          const sub = dialerSubs.find(s => s.id === dialerRenewModal.subId);
          const baseDate = dialerRenewModal.currentExpiry ? new Date(dialerRenewModal.currentExpiry) : new Date();
          const newExpiry = new Date(baseDate.getTime() + dialerRenewModal.additionalDays * 86400000);
          return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setDialerRenewModal({ isOpen: false, subId: null, additionalDays: 30, currentExpiry: null, productId: DIALER_PRODUCT_IDS[0] })}>
              <div className="fixed inset-0 bg-black/50" />
              <div className="relative bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-[420px] animate-[modalSlideIn_0.15s_ease-out]" onClick={e => e.stopPropagation()}>
                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-base font-semibold text-slate-900">Renew Subscription</h2>
                    <button onClick={() => setDialerRenewModal({ isOpen: false, subId: null, additionalDays: 30, currentExpiry: null, productId: DIALER_PRODUCT_IDS[0] })} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors -mr-1">
                      <X className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">Extend the subscription duration.</p>
                </div>
                {sub && (
                  <div className="mx-6 mb-4 bg-slate-50 rounded-xl px-4 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700 shrink-0">
                          {(sub.agent_name ?? 'U').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{sub.agent_name}</p>
                          <p className="text-[11px] text-slate-400">{sub.product_name}</p>
                        </div>
                      </div>
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${dialerStatusBadge[sub.status]}`}>
                        {dialerStatusLabel[sub.status]}
                      </span>
                    </div>
                    <div className="mt-2.5 pt-2.5 border-t border-slate-200/60 grid grid-cols-2 gap-3">
                      <div>
                        <p className="text-[10px] text-slate-400">Paid</p>
                        <p className="text-xs font-medium text-slate-600">{formatDialerDate(sub.created_at)}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-slate-400">Current Expiry</p>
                        <p className="text-xs font-medium text-slate-600">{formatDialerDateTime(dialerRenewModal.currentExpiry)}</p>
                      </div>
                    </div>
                  </div>
                )}
                <div className="px-6 pb-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-600 mb-1">Additional Days</label>
                    <input
                      type="number"
                      min={1}
                      value={dialerRenewModal.additionalDays}
                      onChange={e => setDialerRenewModal(prev => ({ ...prev, additionalDays: Math.max(1, parseInt(e.target.value) || 1) }))}
                      autoFocus
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400 transition-all"
                    />
                  </div>
                  <div className="bg-blue-50 rounded-lg px-3.5 py-2.5 flex items-center justify-between">
                    <span className="text-[11px] font-medium text-blue-600">New expiry</span>
                    <span className="text-xs font-semibold text-blue-800">{newExpiry.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York', timeZoneName: 'short' })}</span>
                  </div>
                </div>
                <div className="px-6 pb-6 flex gap-3">
                  <button onClick={() => setDialerRenewModal({ isOpen: false, subId: null, additionalDays: 30, currentExpiry: null, productId: DIALER_PRODUCT_IDS[0] })} className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">Cancel</button>
                  <button onClick={handleDialerRenew} disabled={dialerRenewModal.additionalDays < 1 || dialerActionLoading === dialerRenewModal.subId} className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {dialerActionLoading === dialerRenewModal.subId && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                    Renew Subscription
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Change Product Modal */}
        {dialerChangeProductModal.isOpen && (() => {
          const sub = dialerChangeProductModal.subId ? dialerSubs.find(s => s.id === dialerChangeProductModal.subId) : null;
          return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setDialerChangeProductModal({ isOpen: false, subId: null, currentProductId: 0, newProductId: 0 })}>
              <div className="fixed inset-0 bg-black/50" />
              <div className="relative bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-[420px] animate-[modalSlideIn_0.15s_ease-out]" onClick={e => e.stopPropagation()}>
                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-base font-semibold text-slate-900">Change Product</h2>
                    <button onClick={() => setDialerChangeProductModal({ isOpen: false, subId: null, currentProductId: 0, newProductId: 0 })} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors -mr-1">
                      <X className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">Switch this subscription to a different dialer product.</p>
                </div>
                {sub && (
                  <div className="mx-6 mb-4 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 shrink-0">
                      {(sub.agent_name ?? 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{sub.agent_name}</p>
                      <p className="text-[11px] text-slate-400 truncate">Current: {sub.product_name}</p>
                    </div>
                  </div>
                )}
                <div className="px-6 pb-4 space-y-1.5">
                  {DIALER_PRODUCT_IDS.map(pid => (
                    <button
                      key={pid}
                      type="button"
                      onClick={() => setDialerChangeProductModal(prev => ({ ...prev, newProductId: pid }))}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl text-sm transition-all ${
                        dialerChangeProductModal.newProductId === pid
                          ? 'bg-indigo-50 border-2 border-indigo-400 text-indigo-700'
                          : pid === dialerChangeProductModal.currentProductId
                            ? 'bg-slate-50 border border-slate-200 text-slate-400'
                            : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <div className={`w-2.5 h-2.5 rounded-full ${dialerChangeProductModal.newProductId === pid ? 'bg-indigo-500' : 'bg-slate-300'}`} />
                        <span className="font-medium">{DIALER_PRODUCT_NAMES[pid]}</span>
                        {pid === dialerChangeProductModal.currentProductId && <span className="text-[10px] text-slate-400 ml-1">(current)</span>}
                      </div>
                      <span className={`text-xs ${dialerChangeProductModal.newProductId === pid ? 'text-indigo-500' : 'text-slate-400'}`}>${DIALER_PRODUCT_PRICES[pid]}</span>
                    </button>
                  ))}
                </div>
                <div className="px-6 pb-6 flex gap-3">
                  <button onClick={() => setDialerChangeProductModal({ isOpen: false, subId: null, currentProductId: 0, newProductId: 0 })} className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">Cancel</button>
                  <button
                    onClick={handleDialerChangeProduct}
                    disabled={dialerChangeProductModal.newProductId === dialerChangeProductModal.currentProductId || dialerActionLoading === dialerChangeProductModal.subId}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {dialerActionLoading === dialerChangeProductModal.subId && <RefreshCw className="w-3.5 h-3.5 animate-spin" />}
                    Switch Product
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Edit States Modal */}
        {dialerEditStatesModal.isOpen && (() => {
          const sub = dialerEditStatesModal.subId ? dialerSubs.find(s => s.id === dialerEditStatesModal.subId) : null;
          const ALL_US_STATES = [
            'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
            'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
            'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
            'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
            'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
            'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
            'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
          ];
          const selectedStates = dialerEditStatesModal.states
            .split(',').map(s => s.trim()).filter(Boolean);
          const toggleState = (state: string) => {
            const updated = selectedStates.includes(state)
              ? selectedStates.filter(s => s !== state)
              : [...selectedStates, state];
            setDialerEditStatesModal(prev => ({ ...prev, states: updated.join(', ') }));
          };
          const selectAll = () => setDialerEditStatesModal(prev => ({ ...prev, states: ALL_US_STATES.join(', ') }));
          const clearAll = () => setDialerEditStatesModal(prev => ({ ...prev, states: '' }));
          return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => setDialerEditStatesModal({ isOpen: false, subId: null, orderId: null, states: '' })}>
              <div className="fixed inset-0 bg-black/50" />
              <div className="relative bg-white rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.12)] w-full max-w-[540px] max-h-[90vh] flex flex-col animate-[modalSlideIn_0.15s_ease-out]" onClick={e => e.stopPropagation()}>
                <div className="px-6 pt-6 pb-4">
                  <div className="flex items-center justify-between mb-1">
                    <h2 className="text-base font-semibold text-slate-900">Edit States</h2>
                    <button onClick={() => setDialerEditStatesModal({ isOpen: false, subId: null, orderId: null, states: '' })} className="w-7 h-7 rounded-lg hover:bg-slate-100 flex items-center justify-center transition-colors -mr-1">
                      <X className="w-4 h-4 text-slate-400" />
                    </button>
                  </div>
                  <p className="text-xs text-slate-400">Select states for this subscription.</p>
                </div>
                {sub && (
                  <div className="mx-6 mb-3 bg-slate-50 rounded-xl px-4 py-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-bold text-blue-700 shrink-0">
                      {(sub.agent_name ?? 'U').charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{sub.agent_name}</p>
                      <p className="text-[11px] text-slate-400 truncate">{sub.product_name}</p>
                    </div>
                  </div>
                )}
                <div className="px-6 pb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-500">{selectedStates.length} of {ALL_US_STATES.length} selected</span>
                  <div className="flex gap-2">
                    <button onClick={selectAll} className="text-[11px] font-medium text-blue-600 hover:text-blue-700">Select All</button>
                    <span className="text-slate-300">|</span>
                    <button onClick={clearAll} className="text-[11px] font-medium text-slate-400 hover:text-slate-600">Clear</button>
                  </div>
                </div>
                <div className="px-6 pb-4 overflow-y-auto max-h-[45vh]">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                    {ALL_US_STATES.map(state => {
                      const isSelected = selectedStates.includes(state);
                      return (
                        <button
                          key={state}
                          type="button"
                          onClick={() => toggleState(state)}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-medium text-left transition-all ${
                            isSelected
                              ? 'bg-blue-50 text-blue-700 border border-blue-300'
                              : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {isSelected && <span className="mr-1">✓</span>}{state}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="px-6 py-4 border-t border-slate-100 flex gap-3">
                  <button onClick={() => setDialerEditStatesModal({ isOpen: false, subId: null, orderId: null, states: '' })} className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors">Cancel</button>
                  <button
                    onClick={handleDialerSaveStates}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    Save States ({selectedStates.length})
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {activeTab === 'store-orders' && (
          <div>
            {storeOrdersLoading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-5 h-5 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
              </div>
            ) : (() => {
              const stats = getStoreOrderStats(storeOrders);
              const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
              return (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-200 rounded-lg overflow-hidden mb-6">
                    {[
                      { label: 'Total Orders', value: String(stats.count) },
                      { label: 'Total Revenue', value: money(stats.revenue) },
                      { label: 'Orders This Month', value: String(stats.thisMonthCount) },
                      { label: 'Revenue This Month', value: money(stats.thisMonthRevenue) },
                    ].map((s) => (
                      <div key={s.label} className="bg-white p-4">
                        <p className="text-[11px] text-slate-400 uppercase tracking-wider font-medium">{s.label}</p>
                        <p className="text-2xl font-bold text-slate-900 mt-1">{s.value}</p>
                      </div>
                    ))}
                  </div>

                  {storeOrders.length === 0 ? (
                    <div className="border border-slate-200 rounded-lg px-5 py-16 text-center">
                      <p className="text-sm text-slate-400">No store orders yet. Paid merch orders will appear here automatically.</p>
                    </div>
                  ) : (
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="overflow-auto">
                        <table className="w-full min-w-[800px] text-sm">
                          <thead>
                            <tr className="border-b border-slate-200 bg-slate-50 text-[11px] text-slate-400 uppercase tracking-wider">
                              <th className="text-left px-5 py-2.5 font-medium">Order</th>
                              <th className="text-left px-3 py-2.5 font-medium">Customer</th>
                              <th className="text-left px-3 py-2.5 font-medium">Items</th>
                              <th className="text-left px-3 py-2.5 font-medium">Date</th>
                              <th className="text-left px-3 py-2.5 font-medium">Fulfillment</th>
                              <th className="text-right px-5 py-2.5 font-medium">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {storeOrders.map((o) => {
                              const isOpen = expandedStoreOrderId === o.id;
                              const itemSummary = o.line_items.map((li) => `${li.quantity}x ${li.title}${li.variant_title ? ` (${li.variant_title})` : ''}`).join(', ');
                              const addr: any = o.shipping_address || {};
                              return (
                                <React.Fragment key={o.id}>
                                  <tr className="hover:bg-slate-50/60 transition-colors cursor-pointer" onClick={() => setExpandedStoreOrderId(isOpen ? null : o.id)}>
                                    <td className="px-5 py-3 font-medium text-slate-900 whitespace-nowrap">{o.order_number || o.shopify_order_id}</td>
                                    <td className="px-3 py-3">
                                      <p className="text-slate-700 truncate max-w-[160px]">{o.customer_name || 'Unknown'}</p>
                                      <p className="text-[11px] text-slate-400 truncate max-w-[160px]">{o.customer_email || '-'}</p>
                                    </td>
                                    <td className="px-3 py-3 text-slate-600 truncate max-w-[240px]">{itemSummary}</td>
                                    <td className="px-3 py-3 text-slate-500 whitespace-nowrap">{new Date(o.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</td>
                                    <td className="px-3 py-3">
                                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded ${o.fulfillment_status === 'fulfilled' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                        {o.fulfillment_status || 'unfulfilled'}
                                      </span>
                                    </td>
                                    <td className="px-5 py-3 text-right font-semibold text-slate-900 whitespace-nowrap tabular-nums">{o.total_amount != null ? money(o.total_amount) : '-'}</td>
                                  </tr>
                                  {isOpen && (
                                    <tr className="bg-slate-50/60">
                                      <td colSpan={6} className="px-5 py-4">
                                        <div className="grid sm:grid-cols-2 gap-6">
                                          <div>
                                            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Items</p>
                                            <ul className="space-y-1">
                                              {o.line_items.map((li, i) => (
                                                <li key={i} className="text-sm text-slate-700">{li.quantity}x {li.title}{li.variant_title ? ` (${li.variant_title})` : ''}{li.price ? ` - $${li.price}` : ''}</li>
                                              ))}
                                            </ul>
                                          </div>
                                          <div>
                                            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mb-2">Shipping</p>
                                            <p className="text-sm text-slate-700 leading-relaxed">
                                              {addr.name || o.customer_name || ''}<br />
                                              {addr.address1 || ''}{addr.address2 ? `, ${addr.address2}` : ''}<br />
                                              {[addr.city, addr.province, addr.zip].filter(Boolean).join(', ')}<br />
                                              {addr.country || ''}
                                            </p>
                                            {o.customer_phone && <p className="text-sm text-slate-500 mt-1">{o.customer_phone}</p>}
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {activeTab === 'inbound-inquiries' && <InboundInquiries />}

        {activeTab === 'api-keys' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">API Keys</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Manage API keys for programmatic order fulfillment.
                </p>
              </div>
              <button
                onClick={() => {
                  loadApiKeys();
                  setShowNewKeyModal(true);
                  setNewlyCreatedKey(null);
                  setNewlyCreatedRecoveryCode(null);
                  setNewKeyName('');
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-navy-600 text-white text-sm font-medium rounded-lg hover:bg-navy-700 transition-colors"
              >
                <Key className="w-4 h-4" />
                Generate New Key
              </button>
            </div>

            {/* Endpoint Info Box */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4" />
                Endpoint Details
              </h3>
              <div className="space-y-2">
                <div>
                  <span className="text-xs font-medium text-slate-500 uppercase">Method</span>
                  <p className="text-sm font-mono text-slate-900 mt-0.5">POST</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-slate-500 uppercase">URL</span>
                  <p className="text-sm font-mono text-slate-900 mt-0.5 break-all">{getSupabaseEndpoint()}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-slate-500 uppercase">Auth Header</span>
                  <p className="text-sm font-mono text-slate-900 mt-0.5">Authorization: Bearer {'<YOUR_API_KEY>'}</p>
                </div>
                <div>
                  <span className="text-xs font-medium text-slate-500 uppercase">Request Body</span>
                  <pre className="text-xs font-mono text-slate-700 mt-1 bg-white p-3 rounded-lg border border-slate-200 overflow-x-auto">{`{
  "name": "string",
  "email": "string",
  "productName": "string",
  "googleSheetUrl": "string",
  "orderId": "string"
}`}</pre>
                </div>
              </div>
            </div>

            {/* Keys List */}
            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Active Keys</h3>
                <button
                  onClick={loadApiKeys}
                  className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                >
                  <RefreshCw className="w-3 h-3" />
                  Refresh
                </button>
              </div>
              
              {apiKeysLoading ? (
                <div className="p-16 text-center">
                  <div className="w-5 h-5 border-2 border-slate-200 border-t-slate-900 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-sm text-slate-400">Loading API keys...</p>
                </div>
              ) : apiKeys.length === 0 ? (
                <div className="p-8 text-center">
                  <Key className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">No API keys yet. Generate one to get started.</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {apiKeys.map(key => (
                    <div key={key.id} className="p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium text-sm text-slate-900">{key.name}</span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                              key.is_active 
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' 
                                : 'bg-slate-100 text-slate-500 border border-slate-200'
                            }`}>
                              {key.is_active ? 'Active' : 'Revoked'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                            <span>Created: {formatDateTime(key.created_at)}</span>
                            <span>Expires: {formatDateTime(key.expires_at)}</span>
                            <span>Last used: {formatDateTime(key.last_used_at)}</span>
                          </div>
                        </div>
                        {key.is_active && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewLogs(key.id, key.name)}
                              className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors flex items-center gap-1"
                            >
                              <Clock className="w-3 h-3" />
                              Logs
                            </button>
                            <button
                              onClick={() => {
                                setShowRotateModal({ isOpen: true, keyId: key.id, keyName: key.name });
                                setRotatePassword('');
                                setRotateError('');
                              }}
                              className="px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-md hover:bg-amber-100 transition-colors flex items-center gap-1"
                            >
                              <RotateCcw className="w-3 h-3" />
                              Rotate
                            </button>
                            <button
                              onClick={() => handleRevokeKey(key.id)}
                              className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors flex items-center gap-1"
                            >
                              <Trash2 className="w-3 h-3" />
                              Revoke
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Usage Logs Panel */}
            {selectedKeyLogs && (
              <div className="border border-slate-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-900">
                    Usage Logs: {selectedKeyLogs.keyName}
                  </h3>
                  <button
                    onClick={() => setSelectedKeyLogs(null)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {selectedKeyLogs.logs.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-500">No API calls logged yet.</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-slate-500">Time</th>
                          <th className="px-4 py-2 text-left font-medium text-slate-500">Endpoint</th>
                          <th className="px-4 py-2 text-left font-medium text-slate-500">Status</th>
                          <th className="px-4 py-2 text-left font-medium text-slate-500">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {selectedKeyLogs.logs.map(log => (
                          <tr key={log.id} className="hover:bg-slate-50">
                            <td className="px-4 py-2 text-slate-600 whitespace-nowrap">{formatDateTime(log.created_at)}</td>
                            <td className="px-4 py-2 font-mono text-slate-700">{log.endpoint}</td>
                            <td className="px-4 py-2">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                log.status_code === 200 
                                  ? 'bg-emerald-50 text-emerald-700' 
                                  : log.status_code === 401
                                  ? 'bg-red-50 text-red-700'
                                  : 'bg-amber-50 text-amber-700'
                              }`}>
                                {log.status_code}
                              </span>
                            </td>
                            <td className="px-4 py-2 text-slate-500 max-w-xs truncate">{log.request_summary || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Generate API Key Modal */}
      {showNewKeyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => {
            if (!newlyCreatedKey) { setShowNewKeyModal(false); }
          }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-navy-100 rounded-full flex items-center justify-center">
                  <Key className="w-5 h-5 text-navy-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {newlyCreatedKey ? 'API Key Created' : 'Generate API Key'}
                </h3>
              </div>
              {!newlyCreatedKey && (
                <button onClick={() => setShowNewKeyModal(false)} className="p-2 text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            <div className="px-6 py-5">
              {!newlyCreatedKey ? (
                <>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Key Name</label>
                  <input
                    type="text"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="e.g., Fulfillment Bot"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                    onKeyDown={(e) => e.key === 'Enter' && handleGenerateKey()}
                  />
                  <p className="mt-2 text-xs text-gray-500">
                    Give this key a descriptive name so you know what it's used for.
                  </p>
                </>
              ) : (
                <div className="space-y-4">
                  {/* Warning */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-sm font-medium text-amber-800">
                      Save these values now. They will never be shown again.
                    </p>
                  </div>

                  {/* API Key */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">API Key</label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 relative">
                        <input
                          type={showKeyValue ? 'text' : 'password'}
                          value={newlyCreatedKey}
                          readOnly
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-xs font-mono bg-gray-50 focus:outline-none"
                        />
                        <button
                          onClick={() => setShowKeyValue(!showKeyValue)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showKeyValue ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                      <button
                        onClick={() => copyToClipboard(newlyCreatedKey, 'key')}
                        className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors flex items-center gap-1 ${
                          keyCopied 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {keyCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        {keyCopied ? 'Copied' : 'Copy'}
                      </button>
                    </div>
                  </div>

                  {/* Recovery Code (only on first key) */}
                  {newlyCreatedRecoveryCode && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Recovery Code
                        <span className="ml-2 text-xs font-normal text-amber-600">(Emergency key rotation)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={newlyCreatedRecoveryCode}
                          readOnly
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-xs font-mono bg-gray-50 focus:outline-none"
                        />
                        <button
                          onClick={() => copyToClipboard(newlyCreatedRecoveryCode, 'recovery')}
                          className={`px-3 py-2 text-sm font-medium rounded-lg border transition-colors flex items-center gap-1 ${
                            recoveryCopied 
                              ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {recoveryCopied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          {recoveryCopied ? 'Copied' : 'Copy'}
                        </button>
                      </div>
                      <p className="mt-1 text-xs text-gray-500">
                        Store this recovery code securely. Use it to rotate your API key if you can't log in.
                      </p>
                    </div>
                  )}

                  {/* Endpoint reminder */}
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <p className="text-xs font-medium text-slate-700 mb-1">Endpoint URL</p>
                    <p className="text-xs font-mono text-slate-600 break-all">{getSupabaseEndpoint()}</p>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex gap-3">
              {!newlyCreatedKey ? (
                <>
                  <button
                    onClick={() => setShowNewKeyModal(false)}
                    className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGenerateKey}
                    disabled={!newKeyName.trim()}
                    className="flex-1 px-4 py-2 text-sm font-medium text-white bg-navy-600 rounded-lg hover:bg-navy-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Key className="w-4 h-4" />
                    Generate
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    setShowNewKeyModal(false);
                    setNewlyCreatedKey(null);
                    setNewlyCreatedRecoveryCode(null);
                    setShowKeyValue(false);
                  }}
                  className="w-full px-4 py-2 text-sm font-medium text-white bg-navy-600 rounded-lg hover:bg-navy-700 transition-colors"
                >
                  I've Saved My Key, Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rotate Key Modal */}
      {showRotateModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowRotateModal({ isOpen: false, keyId: '', keyName: '' })} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Rotate API Key</h3>
                  <p className="text-sm text-gray-500">{showRotateModal.keyName}</p>
                </div>
              </div>
              <button onClick={() => setShowRotateModal({ isOpen: false, keyId: '', keyName: '' })} className="p-2 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5">
              <p className="text-sm text-gray-600 mb-4">
                This will revoke the current key and generate a new one. Re-enter your password to confirm.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <input
                type="password"
                value={rotatePassword}
                onChange={(e) => setRotatePassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                onKeyDown={(e) => e.key === 'Enter' && handleRotateKey()}
              />
              {rotateError && (
                <p className="mt-2 text-sm text-red-600">{rotateError}</p>
              )}
            </div>

            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex gap-3">
              <button
                onClick={() => setShowRotateModal({ isOpen: false, keyId: '', keyName: '' })}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRotateKey}
                disabled={!rotatePassword}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" />
                Rotate Key
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fulfillment Modal */}
      {fulfillmentModal.isOpen && fulfillmentModal.order && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={closeFulfillmentModal}
          />
          
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${fulfillmentModal.order.fulfilled ? 'bg-navy-100' : 'bg-emerald-100'}`}>
                  {fulfillmentModal.order.fulfilled ? (
                    <Edit3 className="w-5 h-5 text-navy-600" />
                  ) : (
                    <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                  )}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {fulfillmentModal.order.fulfilled ? 'Edit Google Sheet' : 'Fulfill Order'}
                  </h3>
                  <p className="text-sm text-gray-500">{fulfillmentModal.order.product_name}</p>
                </div>
              </div>
              <button
                onClick={closeFulfillmentModal}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="px-6 py-5">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-1">
                  <strong>Customer:</strong> {fulfillmentModal.order.customer_name}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Email:</strong> {fulfillmentModal.order.customer_email}
                </p>
              </div>
              
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Google Sheet URL <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="url"
                  value={fulfillmentModal.googleSheetUrl}
                  onChange={(e) => setFulfillmentModal(prev => ({ ...prev, googleSheetUrl: e.target.value }))}
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
                <p className="mt-2 text-xs text-gray-500">
                  Paste the Google Sheet URL containing the leads. The agent will be able to access this from their account.
                </p>
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex gap-3">
              <button
                onClick={closeFulfillmentModal}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={fulfillOrderWithSheet}
                className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-xl transition-colors flex items-center justify-center gap-2 ${
                  fulfillmentModal.order?.fulfilled 
                    ? 'bg-navy-600 hover:bg-navy-700' 
                    : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {fulfillmentModal.order?.fulfilled ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Save Changes
                  </>
                ) : isLeadOrder(fulfillmentModal.order) ? (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Send to Fulfillment
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Mark Fulfilled
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manual Order Modal */}
      {manualOrderModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={closeManualOrderModal}
          />
          
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] sm:max-h-[95vh] flex flex-col mx-2 sm:mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-200 flex-shrink-0">
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {manualOrderModal.editMode ? 'Edit Manual Order' : 'Create Manual Order'}
                </h3>
                <p className="text-xs text-gray-500">
                  {manualOrderModal.editMode ? 'Update order details' : 'Enter order details for external sales'}
                </p>
              </div>
              <button
                onClick={closeManualOrderModal}
                className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content - Scrollable */}
            <div className="px-4 sm:px-6 py-4 space-y-4 overflow-y-auto flex-1">
              {/* Agent Selection - Full Width */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Select Agent <span className="text-red-500">*</span>
                </label>
                <select
                  value={manualOrderModal.agentId}
                  onChange={(e) => setManualOrderModal(prev => ({ ...prev, agentId: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                >
                  <option value="">Choose an agent...</option>
                  {agents
                    .filter(a => a.role !== 'admin')
                    .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`))
                    .map(agent => (
                      <option 
                        key={agent.user_id} 
                        value={agent.user_id}
                        className="py-2"
                      >
                        {agent.first_name} {agent.last_name} • {agent.email}{agent.created_by === 'admin' ? ' (Manual)' : ''}
                      </option>
                    ))}
                </select>
                {manualOrderModal.agentId && (
                  <p className="mt-1 text-xs text-slate-500">
                    {agents.find(a => a.user_id === manualOrderModal.agentId)?.email}
                  </p>
                )}
              </div>

              {/* Product Type Toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={!manualOrderModal.useCustom}
                    onChange={() => setManualOrderModal(prev => ({ ...prev, useCustom: false }))}
                    className="w-4 h-4 text-navy-600 border-gray-300 focus:ring-navy-500"
                  />
                  <span className="text-sm font-medium text-gray-900">Use Product Catalog</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={manualOrderModal.useCustom}
                    onChange={() => setManualOrderModal(prev => ({ ...prev, useCustom: true }))}
                    className="w-4 h-4 text-navy-600 border-gray-300 focus:ring-navy-500"
                  />
                  <span className="text-sm font-medium text-gray-900">Custom Product</span>
                </label>
              </div>

              {/* Catalog Product Selection */}
              {!manualOrderModal.useCustom ? (
                <>
                  {/* Product Category */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Product Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={manualOrderModal.productCategory}
                      onChange={(e) => setManualOrderModal(prev => ({ 
                        ...prev, 
                        productCategory: e.target.value as any,
                        selectedProductId: null,
                        selectedPackageIndex: null
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                    >
                      <option value="Fresh Leads">Fresh Leads</option>
                      <option value="Aged Leads">Aged Leads</option>
                      <option value="SMS Leads">SMS Leads</option>
                      <option value="Dialer Technology">Dialer Technology</option>
                    </select>
                  </div>

                  {/* Product Selection */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Select Product <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={manualOrderModal.selectedProductId || ''}
                      onChange={(e) => setManualOrderModal(prev => ({ 
                        ...prev, 
                        selectedProductId: e.target.value ? parseInt(e.target.value) : null,
                        selectedPackageIndex: null
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                    >
                      <option value="">Choose a product...</option>
                      {getAvailableProducts().map(product => (
                        <option key={product.id} value={product.id}>
                          {product.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Quantity Package Selection */}
                  {manualOrderModal.selectedProductId && (
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Quantity & Price <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={manualOrderModal.selectedPackageIndex !== null ? manualOrderModal.selectedPackageIndex : ''}
                        onChange={(e) => setManualOrderModal(prev => ({ 
                          ...prev, 
                          selectedPackageIndex: e.target.value ? parseInt(e.target.value) : null
                        }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                      >
                        <option value="">Choose quantity...</option>
                        {getSelectedProduct()?.pricing.packages.map((pkg, index) => (
                          <option key={index} value={index}>
                            {pkg.quantity} {manualOrderModal.productCategory === 'Dialer Technology' ? 'seat(s)' : 'leads'} - ${pkg.price.toFixed(2)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                </>
              ) : (
                /* Custom Product Entry */
                <>
                  {/* Product Category for Custom */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Product Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={manualOrderModal.productCategory}
                      onChange={(e) => setManualOrderModal(prev => ({ 
                        ...prev, 
                        productCategory: e.target.value as any
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                    >
                      <option value="Fresh Leads">Fresh Leads</option>
                      <option value="Aged Leads">Aged Leads</option>
                      <option value="SMS Leads">SMS Leads</option>
                      <option value="Dialer Technology">Dialer Technology</option>
                    </select>
                  </div>

                  {/* Custom Product Name */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                      Product Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={manualOrderModal.customProductName}
                      onChange={(e) => setManualOrderModal(prev => ({ ...prev, customProductName: e.target.value }))}
                      placeholder="e.g., Special Medicare Leads Bundle"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                    />
                  </div>

                  {/* Quantity and Price - Custom */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Quantity <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={manualOrderModal.customQuantity || ''}
                        onChange={(e) => setManualOrderModal(prev => ({ ...prev, customQuantity: parseInt(e.target.value) || 0 }))}
                        placeholder="0"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1.5">
                        Price (USD) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={manualOrderModal.customPrice || ''}
                        onChange={(e) => setManualOrderModal(prev => ({ ...prev, customPrice: parseFloat(e.target.value) || 0 }))}
                        placeholder="0.00"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Order Summary - Compact */}
              {((manualOrderModal.useCustom && manualOrderModal.customQuantity > 0 && manualOrderModal.customPrice > 0) || 
                (!manualOrderModal.useCustom && manualOrderModal.selectedPackageIndex !== null)) && (
                <div className="p-3 bg-gradient-to-r from-navy-50 to-slate-50 border border-navy-200 rounded-lg">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex-1 min-w-0 pr-2">
                      <p className="text-xs text-slate-600 mb-0.5">Order Total</p>
                      <p className="font-semibold text-slate-900 truncate">
                        {getCurrentOrderDetails().quantity} × {getCurrentOrderDetails().productName.split(' ').slice(0, 3).join(' ')}
                        {getCurrentOrderDetails().productName.split(' ').length > 3 ? '...' : ''}
                      </p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="text-2xl font-bold text-navy-600">${getCurrentOrderDetails().price.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Row: States & Sheet URL */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* States Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    States <span className="text-gray-400 font-normal text-[10px]">(optional)</span>
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setManualOrderModal(prev => ({ ...prev, showStatesDropdown: !prev.showStatesDropdown }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-left focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent flex items-center justify-between"
                    >
                      <span className={manualOrderModal.states.length === 0 ? 'text-gray-400 text-xs' : 'text-gray-900 text-xs'}>
                        {manualOrderModal.states.length === 0 
                          ? 'Select states...' 
                          : `${manualOrderModal.states.length} selected`}
                      </span>
                      <ChevronDown className="w-3 h-3 text-gray-400" />
                    </button>
                    
                    {manualOrderModal.showStatesDropdown && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        <div className="p-1.5">
                          <div className="flex items-center gap-2 mb-1.5 px-1.5 pb-1.5 border-b border-gray-200">
                            <button
                              type="button"
                              onClick={() => setManualOrderModal(prev => ({ ...prev, states: [...siteConfig.states] }))}
                              className="text-[10px] text-navy-600 hover:underline"
                            >
                              Select All
                            </button>
                            <span className="text-gray-300">|</span>
                            <button
                              type="button"
                              onClick={() => setManualOrderModal(prev => ({ ...prev, states: [] }))}
                              className="text-[10px] text-navy-600 hover:underline"
                            >
                              Clear
                            </button>
                          </div>
                          {siteConfig.states.map(state => (
                            <label key={state} className="flex items-center gap-2 px-1.5 py-1 hover:bg-gray-50 rounded cursor-pointer">
                              <input
                                type="checkbox"
                                checked={manualOrderModal.states.includes(state)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setManualOrderModal(prev => ({ ...prev, states: [...prev.states, state] }));
                                  } else {
                                    setManualOrderModal(prev => ({ ...prev, states: prev.states.filter(s => s !== state) }));
                                  }
                                }}
                                className="w-3 h-3 text-navy-600 border-gray-300 rounded focus:ring-navy-500"
                              />
                              <span className="text-xs text-gray-700">{state}</span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {manualOrderModal.states.length > 0 && (
                    <div className="mt-1 text-[10px] text-slate-600">
                      {manualOrderModal.states.slice(0, 3).join(', ')}{manualOrderModal.states.length > 3 ? ` +${manualOrderModal.states.length - 3}` : ''}
                    </div>
                  )}
                </div>

                {/* Google Sheet URL */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Google Sheet <span className="text-gray-400 font-normal text-[10px]">(optional)</span>
                  </label>
                  <input
                    type="url"
                    value={manualOrderModal.googleSheetUrl}
                    onChange={(e) => setManualOrderModal(prev => ({ ...prev, googleSheetUrl: e.target.value }))}
                    placeholder="https://docs.google.com/..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                  />
                  <p className="mt-1 text-[10px] text-gray-500">
                    Auto-marks as fulfilled
                  </p>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  Notes <span className="text-gray-400 font-normal text-[10px]">(optional)</span>
                </label>
                <textarea
                  value={manualOrderModal.notes}
                  onChange={(e) => setManualOrderModal(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes..."
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-transparent resize-none"
                />
              </div>
            </div>
            
            {/* Footer */}
            <div className="bg-gray-50 px-4 sm:px-6 py-3 border-t border-gray-200 flex gap-3 flex-shrink-0 rounded-b-2xl">
              <button
                onClick={closeManualOrderModal}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitManualOrder}
                className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center justify-center gap-2 ${
                  manualOrderModal.editMode 
                    ? 'bg-amber-600 hover:bg-amber-700' 
                    : 'bg-navy-600 hover:bg-navy-700'
                }`}
              >
                <CheckCircle className="w-4 h-4" />
                {manualOrderModal.editMode ? 'Update Order' : 'Create Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
